import { Fragment, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import api, { API, fmtMoney, setMoneyLocale, applyAccent, safeRedirect, fileUrl, providerDocHref, applyDiscount, fmtDiscount, computeVat, isVatFree, proposalTotals, sectionAmounts, formatAxiosError } from "../lib/api";
import Logo from "../components/Logo";
import SectionView, { ZoomableImage, SectionHeading, ProposalThemeContext } from "../components/ProposalSections";
import PrivacyNotice from "../components/PrivacyNotice";
import ChannelTiles from "../components/ChannelTiles";
import { Check, X, ShieldCheck, Loader2, Download, PenLine, Eraser, CreditCard, MessageCircle, Clock, Upload, Landmark, Tv, FileText, ArrowRight, ArrowLeft, ExternalLink } from "lucide-react";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { LangContext, makeTr, useTr, useLang, dateLocale } from "../lib/publicI18n";

// Style classique sur téléphone : feuille bord à bord (annule la marge px-4 de la page), sans bords latéraux.
export const FULL_BLEED = "max-sm:-mx-4 max-sm:border-x-0 max-sm:rounded-none";

export default function PublicProposal() {
  const { publicId } = useParams();
  const [p, setP] = useState(null);
  // Marque blanche : onglet = titre de la proposition (+ société), jamais « Acteio ».
  useDocumentTitle(
    p?.title ? (p.brand?.name || p.company ? `${p.title} · ${p.brand?.name || p.company}` : p.title) : p?.language === "en" ? "Proposal" : "Proposition",
    false,
  );
  const [err, setErr] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  // Paiement à l'année souhaité (offres avec TV) : null = au mois, 'tv' = TV seule, 'all' = toute l'offre. Préférence.
  const [annualPayment, setAnnualPayment] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [deposit, setDeposit] = useState(null); // null | checking | paid | pending | cancel
  const [depositInfo, setDepositInfo] = useState(null); // {amount, currency} after acceptance
  const [paying, setPaying] = useState(false);
  const [questionOpen, setQuestionOpen] = useState(false);
  const [questionText, setQuestionText] = useState("");
  const [questionSent, setQuestionSent] = useState(false);
  const [withdrawalOpen, setWithdrawalOpen] = useState(false);
  const [sepaBanner, setSepaBanner] = useState(null); // null | checking | cancel
  const [actionError, setActionError] = useState(""); // erreur serveur sur une décision (refus, dossier verrouillé…)
  const sectionRefs = useRef({});
  const tocRefs = useRef({}); // blocs de la page listés au sommaire (premium) : chaînes, récapitulatif, étapes, conditions
  const timeAccum = useRef({});
  const currentSection = useRef(null);
  const lastTs = useRef(Date.now());

  // Visiteur INTERNE : un utilisateur Acteio connecté (owner/membre) qui ouvre le lien
  // public depuis le même navigateur ne doit PAS gonfler les stats du prospect. Un token
  // Acteio en localStorage ⇒ interne ; un prospect n'en a jamais → on n'enregistre alors
  // aucun suivi (vues, temps de lecture, scroll). L'affichage de la proposition, lui, reste normal.
  const isInternalViewer = typeof window !== "undefined" && !!localStorage.getItem("acteio_token");

  useEffect(() => () => setMoneyLocale("fr"), []); // montants du back-office : jamais laissés en anglais

  useEffect(() => {
    api.get(`/public/proposals/${publicId}`).then((r) => {
      setMoneyLocale(r.data?.language);
      setP(r.data);
      if (r.data?.brand?.accent_hsl) applyAccent(r.data.brand.accent_hsl);
    }).catch(() => setErr(true));
  }, [publicId]);

  // Lien « Finaliser le dossier » d'un dossier groupé (`/p/<id>#dossier`) : le panneau n'existe qu'une fois la proposition chargée, le
  // navigateur ne sait pas y aller seul.
  const hashScrolled = useRef(false);
  useEffect(() => {
    if (!p || hashScrolled.current || window.location.hash !== "#dossier") return;
    const el = document.getElementById("dossier");
    if (!el) return;
    hashScrolled.current = true;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [p]);

  // Track time per section
  useEffect(() => {
    if (!p || isInternalViewer) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // commit previous
          commitTime();
          currentSection.current = entry.target.dataset.sid;
          lastTs.current = Date.now();
        }
      });
    }, { threshold: 0.5 });

    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));

    const commitTime = () => {
      if (currentSection.current) {
        const sid = currentSection.current;
        const dur = (Date.now() - lastTs.current) / 1000;
        timeAccum.current[sid] = (timeAccum.current[sid] || 0) + dur;
        lastTs.current = Date.now();
      }
    };

    const flush = () => {
      commitTime();
      Object.entries(timeAccum.current).forEach(([sid, dur]) => {
        if (dur < 0.5) return;
        const sec = (p.sections || []).find((s) => s.id === sid);
        api.post(`/public/proposals/${publicId}/track`, {
          type: "time", section_id: sid, section_title: sec?.title || "Section", duration: Math.round(dur),
        }).catch(() => {});
      });
      timeAccum.current = {};
    };

    const interval = setInterval(flush, 8000);
    window.addEventListener("beforeunload", flush);
    return () => { flush(); clearInterval(interval); observer.disconnect(); window.removeEventListener("beforeunload", flush); };
  }, [p, publicId, isInternalViewer]);

  // Sommaire : surligne la partie à l'écran (sections et blocs de la page), pour tous les visiteurs. Bande d'activation
  // vers le tiers haut de l'écran : fonctionne aussi pour une partie plus haute que l'écran (un seuil de 50 % visible,
  // comme pour le suivi de lecture ci-dessus, ne s'atteint jamais sur une longue section).
  useEffect(() => {
    if (!p || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (e.isIntersecting) setActiveSection(e.target.dataset.sid || e.target.dataset.toc);
    }), { rootMargin: "-30% 0px -60% 0px" });
    [...Object.values(sectionRefs.current), ...Object.values(tocRefs.current)].forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [p]);

  // Première ouverture (compteur de vues + notification) & profondeur de scroll.
  // Le backend garde first_view idempotent (guard atomique firstViewedAt) et ne
  // retient le scroll que s'il dépasse le max enregistré.
  useEffect(() => {
    if (!p || isInternalViewer) return;
    api.post(`/public/proposals/${publicId}/track`, { type: "open" }).catch(() => {});
    let maxScroll = 0;
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const pct = h > 0 ? Math.min(100, Math.round((window.scrollY / h) * 100)) : 100;
      if (pct > maxScroll) maxScroll = pct;
    };
    const sendScroll = () => {
      if (maxScroll > 0) api.post(`/public/proposals/${publicId}/track`, { type: "scroll", scroll: maxScroll }).catch(() => {});
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    const scrollInterval = setInterval(sendScroll, 8000);
    window.addEventListener("beforeunload", sendScroll);
    return () => { sendScroll(); window.removeEventListener("scroll", onScroll); clearInterval(scrollInterval); window.removeEventListener("beforeunload", sendScroll); };
  }, [p, publicId, isInternalViewer]);

  const startStripeDeposit = async () => {
    try {
      const { data } = await api.post(`/public/proposals/${publicId}/deposit-checkout`);
      if (data.checkout_url) { safeRedirect(data.checkout_url); return true; }
    } catch (e) { /* retryable */ }
    return false;
  };

  const startPaypalDeposit = async () => {
    try {
      const { data } = await api.post(`/public/proposals/${publicId}/paypal-checkout`);
      if (data.approval_url) { safeRedirect(data.approval_url); return true; }
    } catch (e) { /* retryable */ }
    return false;
  };

  const pollStripeDeposit = async (sid, attempts = 0) => {
    if (attempts >= 8) { setDeposit("pending"); return; }
    try {
      const { data } = await api.get(`/public/payments/status/${sid}`);
      if (data.status === "paid") { setDeposit("paid"); window.history.replaceState({}, "", window.location.pathname); return; }
      if (data.status === "expired") { setDeposit("pending"); return; }
    } catch (e) { /* retry */ }
    setTimeout(() => pollStripeDeposit(sid, attempts + 1), 2000);
  };

  const capturePaypalDeposit = async (orderId) => {
    try {
      const { data } = await api.post(`/public/payments/paypal-capture/${orderId}`);
      if (data.status === "paid") { setDeposit("paid"); window.history.replaceState({}, "", window.location.pathname); return; }
    } catch (e) { /* capture failed */ }
    setDeposit("pending");
  };

  // Recharge silencieuse (dépôt de pièce, retour du mandat SEPA) : ne touche jamais `err` — un
  // échec transitoire de rafraîchissement ne doit pas faire disparaître une proposition déjà affichée.
  const reloadProposal = async () => {
    try {
      const { data } = await api.get(`/public/proposals/${publicId}`);
      setMoneyLocale(data?.language);
      setP(data);
      if (data?.brand?.accent_hsl) applyAccent(data.brand.accent_hsl);
    } catch (e) { /* on garde l'état affiché */ }
  };

  // Retour du mandat SEPA (?sepa=done) : le webhook GoCardless peut ne pas être encore arrivé —
  // même cadence que le polling d'acompte (8 tentatives / 2 s) puis rechargement de la proposition.
  const pollSepaStatus = async (attempts = 0) => {
    if (attempts >= 8) { await reloadProposal(); setSepaBanner(null); return; }
    try {
      const { data } = await api.get(`/public/proposals/${publicId}/sepa-status`);
      if (data.status) { await reloadProposal(); setSepaBanner(null); return; }
    } catch (e) { /* retry */ }
    setTimeout(() => pollSepaStatus(attempts + 1), 2000);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    const paypalResult = params.get("paypal_result");
    const paypalToken = params.get("token");
    const dep = params.get("deposit");
    const sepa = params.get("sepa");
    if (dep === "cancel" || paypalResult === "cancelled") {
      setDeposit("cancel");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (paypalResult === "success" && paypalToken) {
      setDeposit("checking");
      capturePaypalDeposit(paypalToken);
    } else if (sid) {
      setDeposit("checking");
      pollStripeDeposit(sid, 0);
    }
    if (sepa === "done") {
      setSepaBanner("checking");
      window.history.replaceState({}, "", window.location.pathname);
      pollSepaStatus(0);
    } else if (sepa === "cancel") {
      setSepaBanner("cancel");
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Renvoie {ok:true} ou {ok:false, message} plutôt que de laisser échapper l'erreur : les DEUX
  // appelants (refus, signature) doivent pouvoir afficher le message serveur (ex. CGV non
  // acceptées, dossier déjà verrouillé) sans laisser une promesse rejetée non gérée.
  const decide = async (status, payload = {}) => {
    setDeciding(true);
    try {
      const { data } = await api.post(`/public/proposals/${publicId}/decision`, { status, ...payload });
      setP((prev) => ({ ...prev, status: data.status }));
      // Le dossier (date d'ouverture du mandat SEPA, étape « signature ») est posé par la décision :
      // sans relecture, l'état d'avant signature proposerait le mandat avant J+8.
      reloadProposal();
      setSignOpen(false);
      if (data.deposit_required) {
        setDepositInfo({ amount: data.deposit_amount, currency: data.deposit_currency || p.currency });
      }
      return { ok: true };
    } catch (e) {
      // La proposition a changé depuis l'affichage (régénération par le partenaire pendant la
      // lecture) : le serveur répond 409 (content_hash) — recharger pour resynchroniser avant
      // une nouvelle tentative, en plus d'afficher son message.
      if (e.response?.status === 409) reloadProposal();
      return { ok: false, message: formatAxiosError(e) };
    } finally { setDeciding(false); }
  };

  const reject = async () => {
    setActionError("");
    const r = await decide("refusée");
    if (!r.ok) setActionError(r.message);
  };

  const payDeposit = async (method = "stripe") => {
    setPaying(true);
    const ok = method === "paypal" ? await startPaypalDeposit() : await startStripeDeposit();
    if (!ok) setPaying(false);
  };

  const sendQuestion = async () => {
    if (!questionText.trim()) return;
    try {
      await api.post(`/public/proposals/${publicId}/question`, { question: questionText.trim() });
      setQuestionSent(true);
      setQuestionOpen(false);
    } catch (e) { /* silent */ }
  };

  // Config choisie par le prospect : { [item_id]: { quantity, enabled } }. Initialisée depuis les sections.
  const [cfg, setCfg] = useState({});
  useEffect(() => {
    if (!p) return;
    const init = {};
    (p.sections || []).forEach((s) => s.type === "configurable" && (s.items || []).forEach((it) => {
      init[it.id] = { quantity: Number(it.quantity) || 0, enabled: it.optional ? (it.enabled !== false) : true };
    }));
    setCfg(init);
  }, [p]);
  // Premium : la barre du bas s'efface tant que le bouton « Signer » de l'arrivée est à l'écran (une seule action
  // visible à la fois). Sans IntersectionObserver (vieux navigateurs, tests), la barre reste affichée.
  // Zone observée = entre l'en-tête (64 px) et le haut de la barre (72 px au moins) : un bouton à moitié sous la barre
  // la faisait rester par-dessus lui (vu à 375 × 812).
  const heroCtaRef = useRef(null);
  const [heroCtaInView, setHeroCtaInView] = useState(false);
  useEffect(() => {
    const el = heroCtaRef.current;
    if (!el || typeof IntersectionObserver === "undefined") { setHeroCtaInView(false); return undefined; }
    const io = new IntersectionObserver(([e]) => setHeroCtaInView(e.isIntersecting), { rootMargin: "-64px 0px -72px 0px" });
    io.observe(el);
    return () => { io.disconnect(); setHeroCtaInView(false); };
  }, [p]);
  const locked = p && ["acceptée", "refusée"].includes(p.status); // config figée après décision
  const setLine = (id, patch) => setCfg((c) => ({ ...c, [id]: { ...c[id], ...patch } }));

  // ── Dépendances entre lignes (requires) — miroir de resolveConfiguration serveur ──
  const cfgStateOf = (it) => cfg[it.id] || { quantity: Number(it.quantity) || 0, enabled: it.optional ? it.enabled !== false : true };
  const depSatisfied = (s, it, seen = new Set()) => {
    if (!it.requires) return true;
    if (seen.has(it.id)) return true; // cycle de config : ne pas bloquer
    seen.add(it.id);
    const parent = (s.items || []).find((x) => x.id === it.requires);
    if (!parent) return true;
    const pc = cfgStateOf(parent);
    return pc.enabled !== false && (Number(pc.quantity) || 0) >= 1 && depSatisfied(s, parent, seen);
  };

  // Langue encore inconnue (lien invalide) : message dans les deux langues.
  if (err) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Proposition introuvable. <span lang="en" className="ml-1">Proposal not found.</span></div>;
  if (!p) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const lang = p.language === "en" ? "en" : "fr";
  const tr = makeTr(lang);
  const sections = p.sections || [];
  // Totaux ventilés one-shot / récurrent (mensuel), toujours affichés SÉPARÉMENT (jamais
  // additionnés) : une offre peut mélanger un abonnement mensuel et des frais d'installation
  // ponctuels. `resolve` branche l'état interactif de l'offre ajustable (choix du prospect)
  // sur le même algorithme que le serveur (`proposalTotals`, prorata de remise de section
  // mixte — voir proposals.helpers.ts pour la justification).
  const resolveItem = (it, s) => {
    if (s.type !== "configurable") return { enabled: it.enabled !== false, quantity: Number(it.quantity) || 0 };
    const c = cfg[it.id];
    return {
      enabled: (c ? c.enabled : it.enabled !== false) && depSatisfied(s, it),
      quantity: c ? c.quantity : (Number(it.quantity) || 0),
    };
  };
  const totals = proposalTotals(sections, resolveItem);
  // Fiscalité : tarifs saisis HT (calculs serveur inchangés). Si la société a
  // renseigné son taux de TVA, on affiche EN PLUS le bloc TVA/TTC (sur les DEUX montants) ;
  // sinon la mention "Tarifs hors taxes — TVA applicable en sus au taux en vigueur.".
  const vatRate = p.brand?.vat_rate;
  // Pas de TVA (taux 0, ex. Saint-Barthélemy) : prix nets, sans mention HT/TTC ni détail de TVA.
  const vatFree = isVatFree(vatRate);
  const vatOneShot = vatFree ? null : computeVat(totals.oneShot, vatRate);
  const vatRecurring = vatFree ? null : computeVat(totals.recurring, vatRate);
  const HT = vatFree ? "" : tr("HT", "excl. VAT");
  const words = (...parts) => parts.filter(Boolean).join(" ");
  const decided = p.status === "acceptée" || p.status === "refusée";
  const expiryDate = p.expires_at ? new Date(p.expires_at) : null;
  const now = new Date();
  const isExpired = p.expired || (expiryDate && expiryDate < now);
  const expiresInDays = expiryDate ? Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24)) : null;
  const expiresVerysSoon = expiresInDays !== null && expiresInDays >= 0 && expiresInDays <= 3;
  const brandObj = { name: p.brand?.name || p.company || "OXIWI", logo_letter: p.brand?.logo_letter || "O", logo_file_id: p.brand?.logo_file_id };
  // Dépôt de pièces ouvert dès l'envoi (le client peut préparer son dossier avant de signer),
  // tant qu'il reste des pièces requises — jamais sur une proposition en brouillon/expirée.
  const canUploadDocs = ["envoyée", "vue", "acceptée"].includes(p.status) && (p.required_documents || []).length > 0;

  const TYPE_LABEL = lang === "en"
    ? { cover: "Introduction", text: "Overview", goals: "Objectives", process: "Process", stats: "Key figures", team: "Team", pricing: "Pricing", configurable: "Adjustable offer", testimonials: "Testimonials", faq: "FAQ", image: "Visual", video: "Video" }
    : { cover: "Introduction", text: "Présentation", goals: "Objectifs", process: "Processus", stats: "Chiffres clés", team: "Équipe", pricing: "Tarification", configurable: "Offre ajustable", testimonials: "Témoignages", faq: "FAQ", image: "Visuel", video: "Vidéo" };
  // Style choisi par la société : « classic » (défaut) ou « premium » (en-tête d'impact, mur TV, registre luxe).
  const premium = p.brand?.public_theme === "premium";
  // Premium : la couverture devient l'en-tête d'impact (même suivi de lecture) et le récapitulatif contractuel
  // (obligation légale, lu avant de signer) rejoint les conditions ; classique : tout reste dans le document.
  const cover = premium ? sections.find((s) => s.type === "cover") : null;
  const legalSections = premium ? sections.filter((s) => s.generated === "contract_summary") : [];
  const bodySections = sections.filter((s) => s !== cover && !legalSections.includes(s));
  const heroTitle = (cover?.title || "").trim() || p.title;
  const brandName = p.brand?.name || p.company || "";
  // Ce que le client obtient, en un coup d'œil : les services principaux (hors équipements et frais rattachés).
  const services = [...new Set(sections.filter((s) => s.type === "pricing" || s.type === "configurable")
    .flatMap((s) => s.items || []).filter((it) => !it.requires && it.name).map((it) => it.name))].slice(0, 4);
  // « Comment ça se passe » : uniquement les étapes réellement exigées par CETTE proposition, avec l'avancement
  // réel du dossier. Les pièces sont obligatoires POUR signer (refus serveur sinon) : elles viennent avant la signature ;
  // mandat et chaînes suivent la signature ; « Mise en place » n'est pas suivie.
  const signed = p.status === "acceptée";
  // Mandat en attente du délai légal (option de la société, ventes chez le client) : pas encore faisable.
  const sepaAvailable = p.dossier?.sepa?.available_at ? new Date(p.dossier.sepa.available_at) : null;
  const sepaWaiting = !!(sepaAvailable && sepaAvailable.getTime() > Date.now());
  const steps = [
    ...((p.required_documents || []).length ? [{
      short: tr("Pièces", "Documents"), title: tr("Pièces justificatives", "Supporting documents"),
      text: `${p.required_documents.map((d) => d.label).join(", ")} — ${tr("à déposer avant de signer, directement sur cette page (photo ou PDF).", "to upload before signing, right on this page (photo or PDF).")}`,
      done: !!p.dossier?.documents?.complete,
    }] : []),
    { short: tr("Signer", "Sign"), title: tr("Signature en ligne", "Online signature"), text: tr("Quelques instants depuis votre téléphone : signature électronique sécurisée, exemplaire signé envoyé par email.", "A few moments from your phone: secure electronic signature, signed copy sent by email."), done: signed },
    ...(p.requires_sepa_mandate ? [{ short: tr("Mandat", "Mandate"), title: tr("Mandat de prélèvement", "Direct debit mandate"), text: tr("Signé en ligne en quelques clics, sans papier.", "Signed online in a few clicks, paper-free."), done: signed && !!p.dossier?.sepa?.done, waiting: sepaWaiting }] : []),
    ...(tvLines(sections).some((l) => l.channels.mode === "choice") ? [{ short: tr("Chaînes", "Channels"), title: tr("Choix de vos chaînes", "Choose your channels"), text: tr("Vous composez votre sélection après la signature, à votre rythme.", "You put together your selection after signing, at your own pace."), done: signed && !!p.dossier?.channels?.done }] : []),
    { short: tr("Mise en place", "Setup"), title: tr("Mise en place", "Setup"), text: tr(`${brandName || "Votre interlocuteur"} vous recontacte pour organiser la suite.`, `${brandName || "Your advisor"} will get back to you to arrange the next steps.`), done: false },
  ];
  // Étape en cours = la première encore à faire ET faisable (un mandat en attente de délai ne masque pas les chaînes).
  const currentStep = steps.findIndex((st) => !st.done && !st.waiting);
  // Montants en une ligne (arrivée, signature, appel final) : les deux restent séparés, jamais additionnés.
  // Montant vedette du ticket : taille fluide sur téléphone, plus petite au-delà de 10 caractères (« 12 345,60 € »).
  const heroAmount = fmtMoney(totals.recurring > 0 ? totals.recurring : totals.oneShot, p.currency);
  const heroAmountSize = heroAmount.length > 10 ? "text-[length:clamp(1.9rem,10vw,3.1rem)]" : "text-[length:clamp(2.25rem,13vw,3.1rem)]";
  const amountsLine = [totals.recurring > 0 && words(fmtMoney(totals.recurring, p.currency), HT, tr("/ mois", "/ month")),
    totals.oneShot > 0 && words(fmtMoney(totals.oneShot, p.currency), HT, totals.recurring > 0 && tr("une seule fois", "one-off"))].filter(Boolean).join(" · ");
  // Premium : le bloc chaînes suit la grille tarifaire qui porte les formules TV (sinon, après les sections).
  const tvAnchorId = premium && p.tv_channels?.length
    ? sections.find((s) => s.type === "pricing" && s !== cover && (s.items || []).some((it) => it.channels))?.id
    : null;
  const barHidden = premium && heroCtaInView && !questionOpen && !actionError;
  const showDossier = signed && p.dossier && (p.dossier.documents.required.length > 0 || p.dossier.sepa.required || p.dossier.channels?.required);
  const dossierPanel = showDossier && (
    <DossierPanel publicId={publicId} dossier={p.dossier} channels={p.tv_channels || []}
      selections={p.channel_selections || {}} onReload={reloadProposal} premium={premium}
      requiredDocuments={p.required_documents || []} documents={p.documents || []} />
  );
  const totalCells = [
    totals.oneShot > 0 && {
      id: "one-shot", amount: totals.oneShot, vat: vatOneShot,
      label: totals.recurring > 0 ? (premium ? tr("Une seule fois", "One-off") : tr("Montant unique", "One-off amount")) : tr("Montant total", "Total amount"),
      testId: "public-total-one-shot", testIdVat: "public-vat-block-one-shot", testIdTtc: "public-total-ttc-one-shot",
    },
    totals.recurring > 0 && {
      id: "recurring", amount: totals.recurring, vat: vatRecurring, monthly: true,
      label: premium ? tr("Chaque mois", "Every month") : tr("Montant mensuel", "Monthly amount"),
      testId: "public-total-recurring", testIdVat: "public-vat-block-recurring", testIdTtc: "public-total-ttc-recurring",
    },
  ].filter(Boolean);
  // Sommaire : les sections, plus (premium) les blocs de la page — sans eux, une proposition simple n'y montrait que
  // « Votre offre » (l'introduction devient l'arrivée, le récapitulatif contractuel rejoint les conditions).
  const showLegal = premium && (legalSections.length > 0 || p.terms || p.providers?.length > 0 || p.legal?.consumer_like || canUploadDocs);
  const channelsEntry = { id: "bloc-chaines", label: tr("Vos chaînes TV", "Your TV channels") };
  const toc = [
    ...bodySections.flatMap((s) => [
      { id: s.id, label: (s.title || "").trim() || TYPE_LABEL[s.type] || "Section" },
      ...(s.id === tvAnchorId ? [channelsEntry] : []),
    ]),
    ...(premium ? [
      ...(p.tv_channels?.length > 0 && !tvAnchorId ? [channelsEntry] : []),
      { id: "bloc-recapitulatif", label: tr("Récapitulatif", "Summary") },
      { id: "bloc-etapes", label: tr("Et ensuite ?", "What happens next?") },
      ...(showLegal ? [{ id: "bloc-conditions", label: tr("Conditions et documents", "Terms and documents") }] : []),
    ] : []),
  ];
  const scrollToSection = (id) => (sectionRefs.current[id] || tocRefs.current[id])?.scrollIntoView({ behavior: "smooth", block: "start" });
  // Ancre d'un bloc listé au sommaire (premium seulement : le sommaire classique ne liste que les sections).
  const tocAnchor = (id) => (premium ? { ref: (el) => (tocRefs.current[id] = el), "data-toc": id } : {});
  const legalBlocks = (
    <>
      {p.terms && <TermsBlock terms={p.terms} translation={p.terms_translation} publicId={publicId} />}
      {p.providers?.length > 0 && <ProvidersBlock providers={p.providers} />}
      {p.legal?.consumer_like && <WithdrawalBlock legal={p.legal} onOpenForm={() => setWithdrawalOpen(true)} />}
      {canUploadDocs && (
        <DocumentsBlock publicId={publicId} requiredDocuments={p.required_documents} documents={p.documents} onUploaded={reloadProposal} signed={signed} />
      )}
    </>
  );

  return (
    <LangContext.Provider value={lang}>
    <ProposalThemeContext.Provider value={premium ? "premium" : "classic"}>
    <div className={premium ? "proposal-premium min-h-screen pb-40 break-words" : "min-h-screen bg-surface pb-32 break-words"}>
      <header className={premium
        ? "h-16 bg-[#ECE9E3]/90 backdrop-blur-xl sticky top-0 z-40 flex items-center justify-between px-5 sm:px-8"
        : "h-16 bg-white/80 backdrop-blur-xl border-b border-border/60 sticky top-0 z-40 flex items-center justify-between px-4 sm:px-6"}>
        <div className="flex items-center gap-3 min-w-0">
          <Logo brand={brandObj} />
          {p.prospect_brand && (
            <>
              <span className="text-border/60 select-none" aria-hidden>×</span>
              {p.prospect_brand.logo_file_id ? (
                <img
                  src={fileUrl(p.prospect_brand.logo_file_id)}
                  alt={p.prospect_brand.name || ""}
                  className="h-7 w-auto max-w-[100px] object-contain shrink-0"
                />
              ) : (
                <span className="font-heading font-extrabold text-base tracking-tight truncate max-w-[100px]">
                  {p.prospect_brand.name}
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-4">
          <a href={`${API}/public/proposals/${publicId}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="public-download-pdf"
            className={premium
              ? "flex items-center gap-1.5 h-10 px-4 rounded-full bg-white text-[13px] font-semibold text-foreground shadow-[0_1px_2px_rgba(18,18,18,0.06)] hover:text-primary transition-colors"
              : "flex items-center gap-1.5 h-10 text-xs font-semibold text-foreground hover:text-primary transition-colors"}>
            <Download className="w-4 h-4" /> PDF
          </a>
          <span className="hidden sm:inline text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">{p.brand?.tagline || p.company}</span>
        </div>
      </header>

      {deposit && deposit !== "checking" && (
        <div data-testid="deposit-banner" className={`px-6 py-3 text-sm font-medium flex items-center justify-center gap-2 ${deposit === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          <CreditCard className="w-4 h-4" />
          {deposit === "paid" && tr("Acompte réglé avec succès. Merci !", "Deposit paid successfully. Thank you!")}
          {deposit === "cancel" && tr("Paiement de l'acompte annulé — vous pourrez le régler ultérieurement.", "Deposit payment cancelled — you can pay it later.")}
          {deposit === "pending" && tr("Paiement en cours de traitement…", "Payment being processed…")}
        </div>
      )}
      {deposit === "checking" && (
        <div data-testid="deposit-banner" className="px-6 py-3 text-sm font-medium flex items-center justify-center gap-2 bg-primary/5 text-primary">
          <Loader2 className="w-4 h-4 animate-spin" /> {tr("Vérification du paiement de l'acompte…", "Checking the deposit payment…")}
        </div>
      )}
      {sepaBanner === "checking" && (
        <div data-testid="sepa-banner" className="px-6 py-3 text-sm font-medium flex items-center justify-center gap-2 bg-primary/5 text-primary">
          <Loader2 className="w-4 h-4 animate-spin" /> {tr("Vérification du mandat de prélèvement…", "Checking the direct debit mandate…")}
        </div>
      )}
      {sepaBanner === "cancel" && (
        <div data-testid="sepa-banner" className="px-6 py-3 text-sm font-medium flex items-center justify-center gap-2 bg-amber-50 text-amber-700">
          <Landmark className="w-4 h-4" /> {tr("Signature du mandat annulée — vous pourrez la reprendre depuis « Finaliser votre dossier ».", "Mandate signature cancelled — you can resume it from “Complete your setup”.")}
        </div>
      )}
      {isExpired && !decided && (
        <div data-testid="expiry-banner" className="px-6 py-3 text-sm font-medium flex items-center justify-center gap-2 bg-rose-50 text-rose-700">
          <Clock className="w-4 h-4" /> {expiryDate
            ? tr(`Cette proposition a expiré le ${expiryDate.toLocaleDateString("fr-FR")}.`, `This proposal expired on ${expiryDate.toLocaleDateString("en-GB")}.`)
            : tr("Cette proposition a expiré.", "This proposal has expired.")} {tr("Contactez-nous pour obtenir une offre actualisée.", "Contact us for an updated offer.")}
        </div>
      )}
      {expiresVerysSoon && !decided && (
        <div data-testid="expiry-soon-banner" className="px-6 py-3 text-sm font-medium flex items-center justify-center gap-2 bg-amber-50 text-amber-700">
          <Clock className="w-4 h-4" />
          {expiresInDays === 0 ? tr("Cette offre expire aujourd'hui.", "This offer expires today.")
            : tr(`Cette offre expire dans ${expiresInDays} jour${expiresInDays > 1 ? "s" : ""}.`, `This offer expires in ${expiresInDays} day${expiresInDays > 1 ? "s" : ""}.`)}
        </div>
      )}

      {/* Arrivée (premium) : pour qui, la promesse, le « ticket » (prix + ce qui est inclus), le parcours réel et une
          seule action. Mobile : intro, ticket, parcours, bouton ; desktop : intro + parcours | ticket + bouton. */}
      {premium && (
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-3 pb-8 lg:pt-10 lg:pb-14" data-testid="public-hero"
        {...(cover ? { ref: (el) => (sectionRefs.current[cover.id] = el), "data-sid": cover.id } : {})}>
        {/* Colonne bornée sur téléphone : un long titre de proposition (tronqué dans le ticket) élargissait la grille. */}
        <div className="grid gap-6 grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-x-14 lg:gap-y-0">
          <div className="contents lg:block lg:min-w-0">
            <div className="order-1 premium-rise">
              {p.client_name && <p className="text-[13px] font-semibold text-muted-foreground">{tr("Pour", "For")} {p.client_name}</p>}
              <h1 className="font-display font-extrabold text-[2.1rem] leading-[1.04] tracking-[-0.025em] sm:text-5xl lg:text-[3.6rem] mt-2 break-words [text-wrap:balance]">
                {heroTitle}
              </h1>
              {cover?.content && (
                <p className="mt-4 text-[15px] sm:text-lg text-muted-foreground leading-relaxed max-w-xl">{cover.content}</p>
              )}
            </div>
            <Journey steps={steps} current={currentStep} className="order-3 lg:mt-12" />
          </div>
          <div className="contents lg:block">
            <div className="order-2 premium-rise relative rounded-[20px] bg-white shadow-[0_1px_0_rgba(0,0,0,0.04),0_18px_40px_-24px_rgba(18,18,18,0.35)]"
              style={{ animationDelay: "80ms" }} data-testid="hero-price-card">
              <div className="px-5 sm:px-6 pt-5 pb-4">
                <p className="text-[12.5px] font-semibold text-muted-foreground truncate">{p.title}</p>
                <p className="mt-1 flex flex-wrap items-baseline gap-x-2">
                  <span className={`font-display font-extrabold ${heroAmountSize} leading-[1.02] tracking-[-0.03em] tabular-nums`}>
                    {heroAmount}
                  </span>
                  <span className="text-sm font-semibold text-muted-foreground">{totals.recurring > 0 ? words(HT, tr("/ mois", "/ month")) : HT}</span>
                </p>
                {totals.recurring > 0 && totals.oneShot > 0 && (
                  <p className="mt-1.5 text-[13px] text-muted-foreground">+ {words(fmtMoney(totals.oneShot, p.currency), HT, tr("une seule fois", "one-off"))}</p>
                )}
                {!vatFree && (
                  <p className="mt-1.5 text-[11.5px] text-muted-foreground" data-testid="hero-vat">
                    {vatOneShot || vatRecurring
                      ? `${tr(`TVA ${vatRate} % · soit`, `VAT ${vatRate}% · i.e.`)} ${[totals.recurring > 0 && vatRecurring && `${fmtMoney(vatRecurring.ttc, p.currency)} ${tr("TTC / mois", "incl. VAT / month")}`, totals.oneShot > 0 && vatOneShot && `${fmtMoney(vatOneShot.ttc, p.currency)} ${tr("TTC une fois", "incl. VAT one-off")}`].filter(Boolean).join(" · ")}`
                      : tr("Tarifs hors taxes, TVA en sus au taux en vigueur.", "Prices exclude VAT; VAT is added at the applicable rate.")}
                  </p>
                )}
              </div>
              <div className="relative h-5" aria-hidden="true">
                <span className="absolute -left-2.5 top-0 w-5 h-5 rounded-full bg-[#ECE9E3]" />
                <span className="absolute left-5 right-5 top-1/2 border-t-2 border-dashed border-[#E2DDD4]" />
                <span className="absolute -right-2.5 top-0 w-5 h-5 rounded-full bg-[#ECE9E3]" />
              </div>
              {services.length > 0 && (
                <ul className="px-5 sm:px-6 pt-2 pb-4 space-y-2.5" data-testid="hero-services">
                  {services.map((n) => (
                    <li key={n} className="flex items-center gap-2.5 text-[14.5px] leading-snug">
                      <span className="w-[22px] h-[22px] shrink-0 rounded-full bg-primary text-white flex items-center justify-center">
                        <Check className="w-3.5 h-3.5" strokeWidth={3} />
                      </span>
                      {n}
                    </li>
                  ))}
                </ul>
              )}
              <p className="px-5 sm:px-6 py-3 border-t border-[#EFEBE4] text-xs text-muted-foreground">
                {tr("Émise par", "Issued by")} {brandName}
                {expiryDate && !isExpired && ` · ${tr("valable jusqu'au", "valid until")} ${expiryDate.toLocaleDateString(dateLocale(lang), { day: "numeric", month: "long", year: "numeric" })}`}
              </p>
            </div>
            <div className="order-4 lg:mt-5">
              {!decided && !isExpired && (
                <div className="flex flex-col gap-1">
                  <button ref={heroCtaRef} onClick={() => setSignOpen(true)} disabled={deciding} data-testid="hero-accept"
                    className="h-14 rounded-[14px] bg-primary text-white text-[16.5px] font-bold flex items-center justify-center gap-2.5 hover:brightness-110 active:scale-[0.99] transition shadow-[0_16px_30px_-16px_hsl(var(--primary)/0.9)] disabled:opacity-60">
                    {tr("Signer maintenant", "Sign now")} <ArrowRight className="w-[18px] h-[18px]" />
                  </button>
                  {questionSent ? (
                    <p className="h-11 flex items-center justify-center gap-1.5 text-sm font-semibold text-emerald-700"><Check className="w-4 h-4" /> {tr("Question envoyée", "Question sent")}</p>
                  ) : (
                    <button type="button" onClick={() => setQuestionOpen(true)} data-testid="hero-question"
                      className="h-11 text-sm font-semibold text-foreground hover:text-primary transition-colors">
                      {tr("Une question ? Écrivez-nous", "A question? Write to us")}
                    </button>
                  )}
                </div>
              )}
              {signed && (
                <p className="flex items-center gap-2 font-semibold text-emerald-700" data-testid="hero-signed"><Check className="w-4 h-4" /> {tr("Proposition signée — merci !", "Proposal signed — thank you!")}</p>
              )}
              {showDossier && !p.dossier.complete && (
                <button type="button" data-testid="hero-dossier"
                  onClick={() => document.getElementById("dossier")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  className="mt-4 w-full h-14 rounded-[14px] bg-primary text-white text-[16.5px] font-bold flex items-center justify-center gap-2.5 hover:brightness-110 transition">
                  {tr("Finaliser mon dossier", "Complete my setup")} <ArrowRight className="w-[18px] h-[18px]" />
                </button>
              )}
              <p className="mt-3 flex items-center justify-center lg:justify-start gap-1.5 text-[11.5px] text-muted-foreground">
                <ShieldCheck className="w-3.5 h-3.5 shrink-0" /> {tr("Signature électronique sécurisée · exemplaire PDF remis", "Secure electronic signature · PDF copy provided")}
              </p>
            </div>
          </div>
        </div>
      </section>
      )}

      <div className={premium
        ? "max-w-5xl mx-auto px-4 sm:px-8 lg:grid lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-10 lg:items-start"
        : "max-w-5xl mx-auto px-4 py-4 sm:py-10 lg:grid lg:grid-cols-[230px_minmax(0,1fr)] lg:gap-10 lg:items-start"}>
        {/* Propal content — sticky table of contents */}
        <aside className="hidden lg:block sticky top-24" data-testid="proposal-toc">
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-3 px-1">{tr("Contenu de la proposition", "Proposal contents")}</p>
          <nav className="border-l border-border">
            {toc.map((s, i) => (
              <button key={s.id} onClick={() => scrollToSection(s.id)} data-testid={`toc-${s.id}`}
                className={`group w-full text-left flex items-start gap-2.5 pl-3 pr-2 py-1.5 -ml-px border-l-2 transition-all duration-150 ${activeSection === s.id ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"}`}>
                <span className="text-[11px] tabular-nums mt-0.5 opacity-60">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm leading-snug">{s.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        <div className={premium ? "min-w-0 space-y-4" : "min-w-0"}>
          {/* Premium : après signature, le dossier à finaliser vient en premier (le client revient pour ça). */}
          {premium && dossierPanel}
          {/* Premium : pas de feuille unique — chaque section est sa propre carte blanche sur la pierre. Classique sur
              téléphone : la feuille va d'un bord à l'autre (la marge de page + celle de la feuille écrasaient le texte). */}
          <div className={premium ? "max-w-3xl mx-auto lg:mx-0" : `bg-white border border-border shadow-sm rounded-sm animate-fade-up max-w-3xl mx-auto lg:mx-0 overflow-hidden ${FULL_BLEED}`}>
            {p.expired && !decided && (
              <div className={premium ? "bg-amber-50 rounded-[20px] px-5 py-4 mb-4 flex items-center gap-3" : "bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-center gap-3"}>
                <svg className="w-5 h-5 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-amber-800">{tr("Cette proposition a expiré", "This proposal has expired")}</p>
                  <p className="text-xs text-amber-700">{tr("La date limite de réponse est dépassée. Contactez l'émetteur pour obtenir une nouvelle proposition.", "The response deadline has passed. Contact the issuer for a new proposal.")}</p>
                </div>
              </div>
            )}
            {p.expired && (!p.sections || p.sections.length === 0) ? (
              <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
                <svg className="w-16 h-16 text-muted-foreground/30 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h2 className="font-heading text-xl font-bold mb-2">{tr("Proposition expirée", "Proposal expired")}</h2>
                <p className="text-muted-foreground max-w-sm">{tr("La date limite de réponse est dépassée. Contactez directement l'agence pour obtenir une proposition actualisée.", "The response deadline has passed. Contact the agency directly for an updated proposal.")}</p>
              </div>
            ) : (
              <div className={premium ? "space-y-4" : "px-5 py-7 sm:p-8 lg:p-12 space-y-8 sm:space-y-10"}>
                {bodySections.map((s) => (
                  <Fragment key={s.id}>
                  {/* Premium : carte blanche par section ; la grille tarifaire dessine sa propre carte (lignes bord à bord). */}
                  <div ref={(el) => (sectionRefs.current[s.id] = el)} data-sid={s.id} data-testid={`public-section-${s.id}`}
                    className={`scroll-mt-24 ${premium && s.type !== "pricing" ? "rounded-[20px] bg-white p-5 sm:p-8" : ""}`}>
                    {s.type === "configurable" ? (
                      <div>
                        <SectionHeading>{s.title}</SectionHeading>
                        {s.image && <ZoomableImage src={s.image} alt={s.title} className="mb-6" />}
                        <div className="space-y-2" data-testid={`cfg-section-${s.id}`}>
                          {(s.items || []).map((it) => {
                            const c = cfg[it.id] || { quantity: Number(it.quantity) || 0, enabled: it.optional ? it.enabled !== false : true };
                            const okDep = depSatisfied(s, it);
                            const eff = c.enabled && okDep; // état effectif : choix client ET dépendance satisfaite
                            const min = Number(it.min_qty) || 0, max = it.max_qty == null || it.max_qty === "" ? null : Number(it.max_qty);
                            const setQty = (q) => { let n = Math.max(min, Math.round(Number(q) || 0)); if (max != null) n = Math.min(max, n); setLine(it.id, { quantity: n }); };
                            return (
                              <div key={it.id} className={`flex flex-wrap items-center gap-3 p-3 rounded-sm border border-border ${eff ? "" : "opacity-50"}`} data-testid={`cfg-line-${it.id}`}>
                                {it.optional && <input type="checkbox" checked={eff} disabled={locked || !okDep} onChange={(e) => setLine(it.id, { enabled: e.target.checked })} className="w-5 h-5 shrink-0" />}
                                <div className="flex-1 min-w-[160px]">
                                  <p className="font-medium text-sm">{it.name}</p>
                                  {it.description && <p className="text-xs text-muted-foreground">{it.description}</p>}
                                  {!okDep && (
                                    <p className="text-xs text-amber-700 mt-0.5" data-testid={`cfg-requires-hint-${it.id}`}>
                                      {tr("Nécessite", "Requires")} « {(s.items || []).find((x) => x.id === it.requires)?.name || tr("la ligne requise", "the required line")} »
                                    </p>
                                  )}
                                </div>
                                <span className="text-sm font-mono text-muted-foreground">
                                  {words(fmtMoney(Number(it.unit_price) || 0, p.currency), HT)}{it.recurrence === "mensuel" ? tr("/mois", "/month") : ""}
                                  {!!it.discount_type && <span className="ml-1.5 text-emerald-700 font-medium">{fmtDiscount(it.discount_type, it.discount_value, p.currency)}</span>}
                                </span>
                                {it.locked ? (
                                  <span className="text-sm w-24 text-center">× {Number(it.quantity) || 0}</span>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <button disabled={locked || !eff || c.quantity <= min} onClick={() => setQty(c.quantity - 1)} className="w-10 h-10 sm:w-7 sm:h-7 rounded-sm border border-border disabled:opacity-40">−</button>
                                    <input type="number" value={c.quantity} disabled={locked || !eff}
                                      onChange={(e) => setLine(it.id, { quantity: e.target.value === "" ? "" : Number(e.target.value) })}
                                      onBlur={() => setQty(c.quantity === "" ? min : c.quantity)}
                                      className="w-14 text-center bg-muted rounded-sm py-1" data-testid={`cfg-qty-${it.id}`} />
                                    <button disabled={locked || !eff || (max != null && c.quantity >= max)} onClick={() => setQty(c.quantity + 1)} className="w-10 h-10 sm:w-7 sm:h-7 rounded-sm border border-border disabled:opacity-40">+</button>
                                  </div>
                                )}
                                <span className="w-24 text-right font-mono font-semibold">{fmtMoney(eff ? applyDiscount((Number(c.quantity) || 0) * (Number(it.unit_price) || 0), it.discount_type, it.discount_value) : 0, p.currency)}</span>
                              </div>
                            );
                          })}
                          {/* Récapitulatif TOUJOURS affiché (total mis à jour en direct selon
                              les choix du prospect) ; Sous-total/Remise seulement si remise globale.
                              Section mixte (mensuel + unique) : deux totaux séparés, jamais additionnés. */}
                          {(s.items || []).length > 0 && (() => {
                            const subtotal = (s.items || []).reduce((a, it) => {
                              const c = cfg[it.id] || { quantity: Number(it.quantity) || 0, enabled: it.optional ? it.enabled !== false : true };
                              if (!c.enabled || !depSatisfied(s, it)) return a;
                              return a + applyDiscount((Number(c.quantity) || 0) * (Number(it.unit_price) || 0), it.discount_type, it.discount_value);
                            }, 0);
                            const hasSectionDiscount = !!s.discount_type;
                            const { oneShot: sOneShot, recurring: sRecurring } = sectionAmounts(s, resolveItem);
                            const sMixed = sOneShot > 0 && sRecurring > 0;
                            return (
                              <div className="p-3 rounded-sm border border-border bg-surface text-sm space-y-1">
                                {hasSectionDiscount && (
                                  <>
                                    <div className="flex justify-between text-muted-foreground"><span>{words(tr("Sous-total", "Subtotal"), HT)}</span><span className="font-mono">{fmtMoney(subtotal, p.currency)}</span></div>
                                    <div className="flex justify-between text-emerald-700 font-medium"><span>{tr("Remise globale", "Overall discount")}</span><span className="font-mono">{fmtDiscount(s.discount_type, s.discount_value, p.currency)}</span></div>
                                  </>
                                )}
                                {sMixed ? (
                                  <>
                                    <div className="flex justify-between font-semibold"><span>{words("Total", HT)} — {tr("une fois", "one-off")}</span><span className="font-mono">{fmtMoney(sOneShot, p.currency)}</span></div>
                                    <div className="flex justify-between font-semibold"><span>{words("Total", HT)} — {tr("par mois", "per month")}</span><span className="font-mono">{fmtMoney(sRecurring, p.currency)}{tr("/mois", "/month")}</span></div>
                                  </>
                                ) : (
                                  <div className="flex justify-between font-semibold">
                                    <span>{words("Total", HT)}{sRecurring > 0 ? tr(" — par mois", " — per month") : ""}</span>
                                    <span className="font-mono">{fmtMoney(sOneShot + sRecurring, p.currency)}{sRecurring > 0 ? tr("/mois", "/month") : ""}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    ) : (
                      <SectionView section={s} currency={p.currency} clientName={p.client_name} brand={p.brand} />
                    )}
                  </div>
                  {s.id === tvAnchorId && <ChannelsBlock channels={p.tv_channels} lines={tvLines(p.sections)} premium anchor={tocAnchor("bloc-chaines")} />}
                  </Fragment>
                ))}

                {p.tv_channels?.length > 0 && !tvAnchorId && <ChannelsBlock channels={p.tv_channels} lines={tvLines(p.sections)} premium={premium} anchor={tocAnchor("bloc-chaines")} />}

                {/* Récapitulatif final — deux montants SÉPARÉS (jamais additionnés : un
                    abonnement et des frais ponctuels ne forment pas un total qui ait un sens),
                    présentés en PAIRE pour qu'on lise « X une fois, puis Y par mois » d'un coup
                    d'œil. La mention « TVA en sus » est portée UNE seule fois par le bloc : elle
                    était auparavant répétée sous chaque montant (2× ici + 2× dans la barre
                    basse = 4 fois par écran). Si un seul des deux montants existe, sa cellule
                    occupe toute la largeur et le libellé passe à « Montant total ». */}
                <div {...tocAnchor("bloc-recapitulatif")} data-testid="public-totals" className={premium ? "scroll-mt-24 rounded-[20px] bg-[#121212] text-white px-5 py-5 sm:px-8 sm:py-7" : "pt-6 border-t-2 border-foreground"}>
                  {premium && <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-white/60">{tr("Récapitulatif", "Summary")}</p>}
                  {/* Premium : l'abonnement d'abord (le montant qui engage), en lignes sur la carte encre. */}
                  <div className={premium ? "divide-y divide-white/10" : `grid gap-px bg-border border border-border ${totals.oneShot > 0 && totals.recurring > 0 ? "sm:grid-cols-2" : ""}`}>
                    {(premium ? [...totalCells].reverse() : totalCells).map((c) => (
                      <TotalCell key={c.id} {...c} currency={p.currency} vatRate={vatRate} ht={HT} premium={premium} />
                    ))}
                  </div>
                  {!vatFree && !vatOneShot && !vatRecurring && (
                    <p className={`text-xs mt-3 ${premium ? "text-white/60" : "text-muted-foreground"}`} data-testid="public-vat-mention">
                      {tr("Tarifs hors taxes — TVA applicable en sus au taux en vigueur.", "Prices exclude VAT — VAT is added at the applicable rate.")}
                    </p>
                  )}
                </div>

                {(p.signature?.annual_payment || (p.annual_payment_offered && !signed)) && (
                  <AnnualPaymentChoice value={annualPayment} onChange={setAnnualPayment} chosen={p.signature?.annual_payment} premium={premium} />
                )}
                {p.phone_line && signed && <PhoneLineNotice line={p.phone_line} premium={premium} />}

                {premium && <StepsBlock steps={steps} current={currentStep} anchor={tocAnchor("bloc-etapes")} />}
              </div>
            )}
            {!premium && legalBlocks}
          </div>

          {showLegal && (
            <div {...tocAnchor("bloc-conditions")} className="scroll-mt-24 rounded-[20px] bg-white overflow-hidden max-w-3xl mx-auto lg:mx-0" data-testid="public-legal">
              <p className="px-5 sm:px-12 pt-7 text-[12px] font-bold uppercase tracking-[0.06em] text-muted-foreground">{tr("Conditions et documents", "Terms and documents")}</p>
              {legalSections.map((s) => (
                <div key={s.id} ref={(el) => (sectionRefs.current[s.id] = el)} data-sid={s.id} className="px-5 sm:px-12 py-8" data-testid={`public-section-${s.id}`}>
                  <SectionView section={s} currency={p.currency} clientName={p.client_name} brand={p.brand} />
                </div>
              ))}
              {legalBlocks}
            </div>
          )}

          {premium && !decided && !isExpired && (
            <div className="rounded-[20px] bg-[#121212] text-white max-w-3xl mx-auto lg:mx-0 px-6 py-8 sm:px-10 sm:py-10" data-testid="public-final-cta">
              <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-white/60">{tr("Dernière étape", "Final step")}</p>
              <h2 className="font-display font-extrabold text-[2rem] sm:text-4xl mt-2 leading-[1.05] tracking-[-0.02em]">{tr("Tout est prêt de notre côté.", "Everything is ready on our side.")}</h2>
              <p className="mt-3 text-white/70 max-w-lg leading-relaxed">
                {tr(`Signez en ligne en quelques instants, depuis votre téléphone${brandName ? ` : ${brandName} s'occupe du reste.` : "."}`,
                  `Sign online in a few moments, from your phone${brandName ? ` — ${brandName} takes care of the rest.` : "."}`)}
              </p>
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                <button onClick={() => setSignOpen(true)} disabled={deciding} data-testid="final-accept"
                  className="h-14 px-8 rounded-[14px] bg-primary text-white text-[16.5px] font-bold hover:brightness-110 transition flex items-center justify-center gap-2.5 disabled:opacity-60">
                  {tr("Signer maintenant", "Sign now")} <ArrowRight className="w-[18px] h-[18px]" />
                </button>
                <p className="text-sm text-white/60">{amountsLine}</p>
              </div>
            </div>
          )}

          {!premium && dossierPanel}

          <p className={`flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground max-w-3xl mx-auto lg:mx-0 text-center ${premium ? "pt-2" : "mt-6"}`}>
            <ShieldCheck className="w-3.5 h-3.5 shrink-0" /> {tr("Document sécurisé", "Secure document")} · {p.brand?.name || p.company}
            <span aria-hidden>·</span>
            <PrivacyNotice companyName={p.brand?.name || p.company}
              className="py-3 -my-3 underline decoration-dotted underline-offset-2 hover:text-foreground transition-colors" />
          </p>
        </div>
      </div>

      {/* Barre de décision (mêmes actions, mêmes data-testid dans les deux styles). Premium : carte blanche arrondie,
          escamotée tant que le bouton de l'arrivée est à l'écran (invisible = hors tabulation et lecteurs d'écran). */}
      {premium ? (
        <div className={`fixed bottom-0 inset-x-0 z-40 transition-all duration-300 ${barHidden ? "translate-y-full invisible" : ""}`} data-testid="public-bar">
          <div className="bg-white rounded-t-[20px] shadow-[0_-10px_30px_-18px_rgba(18,18,18,0.45)] overflow-hidden pb-safe">
            {actionError && (
              <div className="bg-rose-600 px-4 py-2.5 text-sm text-white text-center" data-testid="public-action-error">
                {actionError}
              </div>
            )}
            {questionOpen && (
              <div className="border-b border-[#EFEBE4] px-4 py-3">
                <div className="max-w-5xl mx-auto">
                  <p className="text-sm font-bold mb-2">{tr("Votre question à l'équipe", "Your question for the team")}</p>
                  <div className="flex gap-2">
                    <textarea
                      value={questionText}
                      onChange={(e) => setQuestionText(e.target.value)}
                      placeholder={tr("Posez votre question ou partagez une objection…", "Ask your question or share a concern…")}
                      aria-label={tr("Votre question", "Your question")}
                      rows={2}
                      autoFocus
                      className="flex-1 min-w-0 rounded-xl border-[1.5px] border-[#DAD5CC] bg-white px-3 py-2 text-base sm:text-sm resize-none outline-none focus:border-primary"
                    />
                    <div className="flex flex-col gap-1">
                      <button onClick={sendQuestion} disabled={!questionText.trim()}
                        className="h-10 px-4 rounded-xl bg-primary text-white text-sm font-bold hover:brightness-110 disabled:opacity-50">
                        {tr("Envoyer", "Send")}
                      </button>
                      <button onClick={() => setQuestionOpen(false)} className="h-10 px-4 rounded-xl bg-[#F4F1EC] text-sm font-semibold">
                        {tr("Annuler", "Cancel")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Montants et actions sur une ligne ; si les montants ne tiennent pas à côté des boutons (petit écran,
                gros montant), les actions passent dessous, pleine largeur — jamais de texte sous les boutons. */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2.5">
              {decided ? (
                <div className={`w-full text-center py-2 font-bold ${p.status === "acceptée" ? "text-emerald-700" : "text-rose-700"}`} data-testid="public-decided">
                  {p.status === "acceptée" ? tr("✓ Vous avez accepté cette proposition. Merci !", "✓ You have accepted this proposal. Thank you!") : tr("Proposition refusée.", "Proposal declined.")}
                </div>
              ) : (
                <>
                  {/* Les deux montants restent SÉPARÉS (jamais additionnés) ; le détail TTC n'apparaît que si un taux
                      de TVA est renseigné — la mention légale complète est dans le récapitulatif et le PDF. */}
                  <div className="flex-none">
                    <div className="flex items-center gap-3 sm:gap-5">
                      {totals.recurring > 0 && (
                        <div data-testid="public-bar-recurring" className="min-w-0">
                          <p className="hidden sm:block text-[11px] font-semibold text-muted-foreground whitespace-nowrap">{tr("Chaque mois", "Every month")}</p>
                          <p className="font-display font-extrabold text-[21px] sm:text-2xl leading-tight tracking-[-0.02em] whitespace-nowrap tabular-nums">
                            {fmtMoney(totals.recurring, p.currency)}<span className="font-sans text-[11px] font-semibold tracking-normal text-muted-foreground ml-1">{words(HT, tr("/ mois", "/ month"))}</span>
                          </p>
                        </div>
                      )}
                      {totals.oneShot > 0 && totals.recurring > 0 && <div className="hidden sm:block w-px self-stretch bg-[#EFEBE4]" aria-hidden="true" />}
                      {totals.oneShot > 0 && (
                        <div data-testid="public-bar-one-shot" className={totals.recurring > 0 ? "hidden sm:block" : ""}>
                          <p className="hidden sm:block text-[11px] font-semibold text-muted-foreground whitespace-nowrap">
                            {totals.recurring > 0 ? tr("Une seule fois", "One-off") : tr("Montant total", "Total amount")}
                          </p>
                          <p className="font-display font-extrabold text-[21px] sm:text-2xl leading-tight tracking-[-0.02em] whitespace-nowrap tabular-nums">
                            {fmtMoney(totals.oneShot, p.currency)}{HT && <span className="font-sans text-[11px] font-semibold tracking-normal text-muted-foreground ml-1">{HT}</span>}
                          </p>
                        </div>
                      )}
                    </div>
                    {totals.oneShot > 0 && totals.recurring > 0 && (
                      <p className="sm:hidden text-[11.5px] text-muted-foreground whitespace-nowrap">+ {words(fmtMoney(totals.oneShot, p.currency), HT, tr("une seule fois", "one-off"))}</p>
                    )}
                    {(vatOneShot || vatRecurring) && (
                      <p className="hidden sm:block text-[11px] text-muted-foreground mt-0.5">
                        {tr(`TVA ${vatRate} % · TTC`, `VAT ${vatRate}% · incl. VAT`)}{" "}
                        {[totals.oneShot > 0 && vatOneShot && fmtMoney(vatOneShot.ttc, p.currency),
                          totals.recurring > 0 && vatRecurring && `${fmtMoney(vatRecurring.ttc, p.currency)}${tr("/mois", "/month")}`]
                          .filter(Boolean).join(" · ")}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-1 items-center justify-end gap-2">
                    {questionSent ? (
                      <span className="hidden sm:flex items-center gap-1.5 text-sm text-emerald-700 font-semibold px-2"><Check className="w-4 h-4" /> {tr("Question envoyée", "Question sent")}</span>
                    ) : (
                      <button onClick={() => setQuestionOpen((v) => !v)} data-testid="public-question" aria-label={tr("J'ai une question", "I have a question")} title={tr("J'ai une question", "I have a question")}
                        className="shrink-0 flex items-center justify-center gap-1.5 h-11 w-11 sm:w-auto sm:px-4 rounded-xl bg-[#F4F1EC] text-sm font-semibold text-foreground hover:bg-[#EAE5DC] transition-colors">
                        <MessageCircle className="w-[18px] h-[18px]" /><span className="hidden sm:inline">{tr("J'ai une question", "I have a question")}</span>
                      </button>
                    )}
                    <button onClick={reject} disabled={deciding} data-testid="public-reject" aria-label={tr("Refuser", "Decline")} title={tr("Refuser", "Decline")}
                      className="shrink-0 flex items-center justify-center gap-1.5 h-11 w-11 sm:w-auto sm:px-4 rounded-xl bg-[#F4F1EC] text-sm font-semibold text-foreground hover:text-rose-700 hover:bg-rose-50 transition-colors">
                      <X className="w-[18px] h-[18px]" /><span className="hidden sm:inline">{tr("Refuser", "Decline")}</span>
                    </button>
                    <button onClick={() => setSignOpen(true)} disabled={deciding || isExpired} data-testid="public-accept"
                      className="max-sm:flex-1 flex items-center justify-center gap-2 h-12 px-5 sm:px-6 rounded-xl bg-primary text-white font-bold whitespace-nowrap hover:brightness-110 disabled:opacity-50 transition">
                      <span className="sm:hidden">{tr("Signer", "Sign")}</span><span className="hidden sm:inline">{tr("Signer maintenant", "Sign now")}</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-border z-40 pb-safe">
          {actionError && (
            <div className="border-b border-border bg-rose-50 px-4 py-2.5 text-sm text-rose-700 text-center" data-testid="public-action-error">
              {actionError}
            </div>
          )}
          {questionOpen && (
            <div className="border-b border-border bg-muted/30 px-4 py-3 max-w-5xl mx-auto">
              <p className="text-sm font-medium mb-2">{tr("Votre question à l'équipe", "Your question for the team")}</p>
              <div className="flex gap-2">
                <textarea
                  value={questionText}
                  onChange={(e) => setQuestionText(e.target.value)}
                  placeholder={tr("Posez votre question ou partagez une objection…", "Ask your question or share a concern…")}
                  aria-label={tr("Votre question", "Your question")}
                  rows={2}
                  className="flex-1 min-w-0 rounded-sm border border-border bg-white px-3 py-2 text-base sm:text-sm resize-none outline-none focus:ring-1 focus:ring-primary"
                />
                <div className="flex flex-col gap-1">
                  <button onClick={sendQuestion} disabled={!questionText.trim()}
                    className="px-4 py-2 rounded-sm bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50">
                    {tr("Envoyer", "Send")}
                  </button>
                  <button onClick={() => setQuestionOpen(false)} className="px-4 py-2 rounded-sm border border-border text-sm text-muted-foreground hover:bg-muted">
                    {tr("Annuler", "Cancel")}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Téléphone : les montants sur une ligne (deux colonnes), les actions dessous — question et refus en icônes,
              « Accepter & signer » sur la largeur restante (l'ancienne barre empilait trois rangées : 30 % de l'écran).
              Écran large : tout sur une ligne. */}
          <div className="max-w-5xl mx-auto px-4 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-x-5 gap-y-2.5">
            {decided ? (
              <div className={`w-full text-center py-2 font-semibold ${p.status === "acceptée" ? "text-emerald-600" : "text-rose-600"}`} data-testid="public-decided">
                {p.status === "acceptée" ? tr("✓ Vous avez accepté cette proposition. Merci !", "✓ You have accepted this proposal. Thank you!") : tr("Proposition refusée.", "Proposal declined.")}
              </div>
            ) : (
              <>
                <div className="flex-none max-sm:w-full">
                  <div className="flex items-center gap-4 sm:gap-5">
                    {totals.oneShot > 0 && (
                      <div data-testid="public-bar-one-shot" className="max-sm:flex-auto">
                        <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.12em] text-muted-foreground whitespace-nowrap">
                          {totals.recurring > 0 ? tr("Montant unique", "One-off amount") : tr("Montant total", "Total amount")}
                        </p>
                        <p className="font-heading text-lg sm:text-xl font-bold leading-tight mt-0.5 whitespace-nowrap">
                          {fmtMoney(totals.oneShot, p.currency)}
                          {HT && <span className="text-xs font-semibold text-muted-foreground ml-1">{HT}</span>}
                        </p>
                      </div>
                    )}
                    {totals.oneShot > 0 && totals.recurring > 0 && (
                      <div className="w-px self-stretch bg-border" aria-hidden="true" />
                    )}
                    {totals.recurring > 0 && (
                      <div data-testid="public-bar-recurring" className="max-sm:flex-auto">
                        <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.12em] text-muted-foreground whitespace-nowrap">{tr("Montant mensuel", "Monthly amount")}</p>
                        <p className="font-heading text-lg sm:text-xl font-bold leading-tight mt-0.5 whitespace-nowrap">
                          {fmtMoney(totals.recurring, p.currency)}
                          <span className="text-xs font-semibold text-muted-foreground ml-1">{HT ? tr("HT/mois", "excl. VAT/month") : tr("/mois", "/month")}</span>
                        </p>
                      </div>
                    )}
                  </div>
                  {(vatOneShot || vatRecurring) && (
                    <p className="text-[11px] text-muted-foreground mt-1.5">
                      {tr(`TVA ${vatRate} % · TTC`, `VAT ${vatRate}% · incl. VAT`)}{" "}
                      {[totals.oneShot > 0 && vatOneShot && fmtMoney(vatOneShot.ttc, p.currency),
                        totals.recurring > 0 && vatRecurring && `${fmtMoney(vatRecurring.ttc, p.currency)}${tr("/mois", "/month")}`]
                        .filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
                <div className="flex flex-1 justify-end gap-2">
                  {questionSent ? (
                    <span className="flex items-center justify-center gap-1.5 min-w-11 h-11 sm:px-3 text-sm text-emerald-600 font-medium" title={tr("Question envoyée", "Question sent")}>
                      <Check className="w-4 h-4" /><span className="sr-only sm:not-sr-only">{tr("Question envoyée", "Question sent")}</span>
                    </span>
                  ) : (
                    <button onClick={() => setQuestionOpen((v) => !v)} data-testid="public-question" aria-label={tr("J'ai une question", "I have a question")} title={tr("J'ai une question", "I have a question")}
                      className="shrink-0 flex items-center justify-center gap-1.5 border border-border w-11 sm:w-auto sm:px-4 h-11 rounded-sm text-sm text-muted-foreground hover:text-foreground hover:border-foreground transition-all">
                      <MessageCircle className="w-4 h-4" /><span className="hidden sm:inline">{tr("J'ai une question", "I have a question")}</span>
                    </button>
                  )}
                  <button onClick={reject} disabled={deciding} data-testid="public-reject" aria-label={tr("Refuser", "Decline")} title={tr("Refuser", "Decline")}
                    className="shrink-0 flex items-center justify-center gap-1.5 border border-border w-11 sm:w-auto sm:px-5 h-11 rounded-sm font-medium hover:border-destructive hover:text-destructive transition-all">
                    <X className="w-4 h-4" /><span className="hidden sm:inline">{tr("Refuser", "Decline")}</span>
                  </button>
                  <button onClick={() => setSignOpen(true)} disabled={deciding || isExpired} data-testid="public-accept"
                    className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 bg-primary text-white px-4 sm:px-6 h-11 rounded-sm font-semibold whitespace-nowrap hover:bg-primary/90 disabled:opacity-50 transition-all">
                    <PenLine className="w-4 h-4 max-[359px]:hidden" /> {tr("Accepter & signer", "Accept & sign")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <p className="max-w-3xl mx-auto px-6 pb-10 pt-2 text-[11px] leading-relaxed text-muted-foreground/70 text-center" data-testid="public-privacy-notice">
        {tr(`En consultant cette proposition, votre activité de lecture et votre adresse IP sont enregistrées à des fins de
        suivi commercial par ${p.brand?.name || "l'émetteur"}. Vous pouvez demander l'accès ou la suppression de ces
        données auprès de l'émetteur.`, `When you view this proposal, your reading activity and IP address are recorded for sales follow-up purposes by
        ${p.brand?.name || "the issuer"}. You can ask the issuer for access to or deletion of this data.`)}
      </p>

      {signOpen && (
        <SignatureDialog
          premium={premium}
          publicId={publicId}
          stepCount={steps.length}
          signStep={steps.findIndex((st) => st.short === tr("Signer", "Sign")) + 1}
          amounts={amountsLine}
          requiredDocuments={p.required_documents || []}
          documents={p.documents || []}
          onDocsUploaded={reloadProposal}
          fibreRequired={!!p.fibre_number_required}
          fibreNumber={p.fibre_number}
          phoneLine={p.phone_line}
          annualPayment={p.annual_payment_offered ? annualPayment : null}
          deciding={deciding}
          brandName={p.brand?.name || p.company}
          terms={p.terms}
          providers={p.providers || []}
          legal={p.legal}
          onClose={() => setSignOpen(false)}
          onSign={(signer_name, signature_data, extra = {}) => {
            // N'inclure `configuration` que si l'offre a une section ajustable (sinon
            // le chemin d'acceptation reste strictement inchangé côté serveur).
            const hasConfigurable = (p.sections || []).some((s) => s.type === "configurable");
            const configuration = [];
            if (hasConfigurable) (p.sections || []).forEach((s) => s.type === "configurable" && (s.items || []).forEach((it) => {
              const c = cfg[it.id]; if (c) configuration.push({ section_id: s.id, item_id: it.id, quantity: c.quantity, enabled: c.enabled });
            }));
            return decide("acceptée", {
              signer_name, signature_data, consent: true,
              ...(hasConfigurable ? { configuration } : {}),
              content_hash: p.content_hash,
              ...(p.terms ? { terms_accepted: true, terms_version_id: p.terms.id } : {}),
              ...(p.providers?.length ? { providers_accepted: extra.providersAccepted || [] } : {}),
              ...(p.legal?.consumer_like ? { express_start: !!extra.expressStart } : {}),
              ...(extra.fibreNumber ? { fibre_number: extra.fibreNumber } : {}),
              ...(extra.phoneMandate ? { phone_mandate_accepted: true } : {}),
              ...(p.annual_payment_offered && annualPayment ? { annual_payment: annualPayment } : {}),
            });
          }}
        />
      )}

      {depositInfo && deposit !== "paid" && (
        <DepositDialog
          info={depositInfo}
          brand={p.brand}
          totals={totals}
          paying={paying}
          onPay={payDeposit}
          onClose={() => setDepositInfo(null)}
        />
      )}

      {withdrawalOpen && p.legal?.withdrawal_form && (
        <WithdrawalFormDialog text={p.legal.withdrawal_form} onClose={() => setWithdrawalOpen(false)} />
      )}

    </div>
    </ProposalThemeContext.Provider>
    </LangContext.Provider>
  );
}

/**
 * Cellule d'un montant du récapitulatif final : libellé discret, montant en vedette avec son
 * suffixe HT (ou HT/mois), et le détail TVA / Total TTC seulement si un taux est renseigné.
 * Mutualisée entre le montant unique et le montant mensuel — les deux cellules ne diffèrent
 * que par leur libellé, leur suffixe et leurs data-testid ; les dupliquer laissait deux blocs
 * de mise en forme à garder synchronisés à la main.
 */
export function TotalCell({ label, amount, currency, vat, vatRate, ht, monthly = false, testId, testIdVat, testIdTtc, premium = false }) {
  const tr = useTr();
  const suffix = monthly ? tr("/mois", "/month") : "";
  if (!premium) {
    return (
      <div className="bg-background p-5">
        <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
        <p className="font-heading text-3xl font-bold mt-1.5 leading-none" data-testid={testId}>
          {fmtMoney(amount, currency)}
          {(ht || suffix) && <span className="text-sm font-semibold text-muted-foreground ml-1.5">{ht}{suffix}</span>}
        </p>
        {vat && (
          <div className="mt-3 pt-3 border-t border-border/70 space-y-1 text-sm" data-testid={testIdVat}>
            <div className="flex items-center justify-between text-muted-foreground">
              <span>{tr(`TVA ${vatRate} %`, `VAT ${vatRate}%`)}</span>
              <span className="font-mono">{fmtMoney(vat.vat, currency)}</span>
            </div>
            <div className="flex items-center justify-between font-semibold">
              <span>{tr("Total TTC", "Total incl. VAT")}</span>
              <span className="font-mono" data-testid={testIdTtc}>{fmtMoney(vat.ttc, currency)}{suffix}</span>
            </div>
          </div>
        )}
      </div>
    );
  }
  // Premium : une ligne de la carte encre du récapitulatif (libellé à gauche, montant à droite).
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 py-3.5 last:pb-0">
      <p className="text-sm text-white/75 pt-2">{label}</p>
      <div className="text-right ml-auto">
        <p className={`font-display font-extrabold tracking-[-0.02em] leading-none tabular-nums ${monthly ? "text-[2rem]" : "text-[1.45rem]"}`} data-testid={testId}>
          {fmtMoney(amount, currency)}
          {(ht || suffix) && <span className="font-sans text-xs font-semibold tracking-normal text-white/60 ml-1.5">{ht}{suffix}</span>}
        </p>
        {vat && (
          <div className="mt-2 space-y-0.5 text-[12.5px] text-white/65" data-testid={testIdVat}>
            <p>{tr(`TVA ${vatRate} %`, `VAT ${vatRate}%`)} · {fmtMoney(vat.vat, currency)}</p>
            <p className="font-semibold text-white">{tr("Total TTC", "Total incl. VAT")} <span className="tabular-nums" data-testid={testIdTtc}>{fmtMoney(vat.ttc, currency)}{suffix}</span></p>
          </div>
        )}
      </div>
    </div>
  );
}

/** Pastille d'étape : faite (coche), en cours (couleur de la société) ou à venir (contour). */
function StepDot({ state, n, size = "w-7 h-7" }) {
  if (state === "done") {
    return <span className={`${size} shrink-0 rounded-full bg-primary text-white flex items-center justify-center`}><Check className="w-3.5 h-3.5" strokeWidth={3} /></span>;
  }
  return (
    <span className={`${size} shrink-0 rounded-full flex items-center justify-center text-[13px] font-bold ${state === "current"
      ? "bg-primary text-white shadow-[0_0_0_4px_hsl(var(--primary)/0.18)]"
      : "bg-white border-[1.5px] border-[#CFC9BE] text-muted-foreground"}`}>{n}</span>
  );
}

const stepState = (st, i, current) => (st.done ? "done" : i === current ? "current" : "todo");

/** « Votre parcours » (arrivée premium) : les étapes réelles du dossier sur un rail, l'étape en cours mise en avant. */
function Journey({ steps, current, className = "" }) {
  const tr = useTr();
  const inset = `${50 / steps.length}%`;
  return (
    <div className={className} data-testid="public-journey">
      <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-muted-foreground">{tr("Votre parcours", "Your journey")}</p>
      <ol className="relative mt-3 grid" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
        <span aria-hidden="true" className="absolute top-[13px] h-0.5 bg-[#D9D4CB]" style={{ left: inset, right: inset }} />
        {steps.map((st, i) => {
          const state = stepState(st, i, current);
          return (
            <li key={st.short} className="relative flex flex-col items-center gap-1.5 text-center" aria-current={state === "current" ? "step" : undefined}>
              <StepDot state={state} n={i + 1} />
              <span className={`text-[11px] leading-tight ${state === "todo" ? "text-muted-foreground" : "font-bold"}`}>
                {st.short}{state === "done" && <span className="sr-only"> {tr("(fait)", "(done)")}</span>}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** « Et ensuite ? » (premium) : les étapes réellement exigées par la proposition, dans l'ordre, avec leur avancement. */
function StepsBlock({ steps, current, anchor = {} }) {
  const tr = useTr();
  return (
    <div {...anchor} className="scroll-mt-24 rounded-[20px] bg-white overflow-hidden" data-testid="public-steps">
      <div className="px-5 sm:px-8 pt-5 sm:pt-7"><SectionHeading>{tr("Et ensuite ?", "What happens next?")}</SectionHeading></div>
      <ol>
        {steps.map((st, i) => (
          <li key={st.title} className="flex gap-3.5 px-5 sm:px-8 py-3.5 border-t border-[#EFEBE4] first:border-t-0">
            <StepDot state={stepState(st, i, current)} n={i + 1} />
            <div className="min-w-0 pt-0.5">
              <p className="font-bold text-[15px] leading-snug">{st.title}</p>
              <p className="text-[13px] text-muted-foreground mt-0.5 leading-snug">{st.text}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// L'acompte porte TOUJOURS sur le one-shot uniquement (jamais sur un abonnement) :
// le solde restant à régler se calcule donc sur `totals.oneShot`, jamais sur un total
// mélangé. L'abonnement mensuel, lui, n'est jamais concerné par l'acompte — affiché à
// part, à titre d'information, quand il existe.
function DepositDialog({ info, brand, totals, paying, onPay, onClose }) {
  const tr = useTr();
  const solde = Math.max(totals.oneShot - info.amount, 0);
  const pct = brand?.deposit_type === "percentage" && brand?.deposit_value ? `${brand.deposit_value} %` : null;
  const hasStripe = !!brand?.stripe_key_set;
  const hasPaypal = !!brand?.paypal_enabled;
  const bothMethods = hasStripe && hasPaypal;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="deposit-modal">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-5 sm:p-6 animate-fade-up">
        <div className="flex items-center gap-2 mb-1">
          <CreditCard className="w-5 h-5 text-primary" />
          <h2 className="font-heading text-xl font-bold">{tr("Acompte à régler", "Deposit due")}</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">{tr("Votre proposition est acceptée ✓. Pour la valider définitivement, un acompte est demandé.", "Your proposal is accepted ✓. A deposit is required to confirm it.")}</p>

        <div className="rounded-sm border border-border divide-y divide-border mb-5" data-testid="deposit-recap">
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm">{tr("Acompte", "Deposit")}{pct ? ` (${pct})` : ""}</span>
            <span className="font-heading font-bold" data-testid="deposit-amount">{fmtMoney(info.amount, info.currency)}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-muted-foreground">{tr("Solde à régler ultérieurement", "Balance payable later")}</span>
            <span className="text-sm text-muted-foreground">{fmtMoney(solde, info.currency)}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3 bg-foreground text-white">
            <span className="text-sm font-semibold">{tr("Total une fois", "One-off total")}</span>
            <span className="font-heading font-bold">{fmtMoney(totals.oneShot, info.currency)}</span>
          </div>
          {totals.recurring > 0 && (
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-muted-foreground">{tr("Abonnement (facturé séparément)", "Subscription (billed separately)")}</span>
              <span className="text-sm text-muted-foreground">{fmtMoney(totals.recurring, info.currency)}{tr("/mois", "/month")}</span>
            </div>
          )}
        </div>

        {bothMethods && (
          <p className="text-xs text-center text-muted-foreground mb-3">{tr("Choisissez votre mode de paiement", "Choose your payment method")}</p>
        )}

        <div className={`${bothMethods ? "flex flex-col gap-2" : "flex gap-2"}`}>
          {!bothMethods && (
            <button onClick={onClose} disabled={paying} data-testid="deposit-later"
              className="flex-1 h-11 border border-border rounded-sm font-medium hover:bg-muted transition-colors">{tr("Plus tard", "Later")}</button>
          )}

          {hasStripe && (
            <button onClick={() => onPay("stripe")} disabled={paying} data-testid="deposit-pay"
              className={`${bothMethods ? "w-full" : "flex-[1.5]"} h-11 bg-primary text-white rounded-sm font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-1.5`}>
              {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {tr("Payer par carte", "Pay by card")} {!bothMethods && `— ${fmtMoney(info.amount, info.currency)}`}
            </button>
          )}

          {hasPaypal && (
            <button onClick={() => onPay("paypal")} disabled={paying} data-testid="deposit-pay-paypal"
              className={`${bothMethods ? "w-full" : "flex-[1.5]"} h-11 bg-[#003087] text-white rounded-sm font-semibold hover:bg-[#003087]/90 transition-colors flex items-center justify-center gap-1.5`}>
              {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20.067 8.478c.492.315.844.825.945 1.406.46 2.67-1.63 4.777-4.16 4.777H15.6l-.75 4.758H12.6l.32-2.02H9.667L8.4 21.9H5.867l3.2-20.257h5.387c2.52 0 4.2 1.614 4.2 3.886 0 .993-.32 1.934-.92 2.673.427.04.84.133 1.4.276zm-5.76 4.383c1.334 0 2.4-.95 2.4-2.107 0-.72-.48-1.26-1.2-1.26H13.2l-.48 3.367h1.587zm1.2-6.24c1.334 0 2.4-.95 2.4-2.107 0-.72-.48-1.26-1.2-1.26h-2.4l-.48 3.367h1.68z" />
                </svg>
              )}
              {tr("Payer via PayPal", "Pay with PayPal")} {!bothMethods && `— ${fmtMoney(info.amount, info.currency)}`}
            </button>
          )}

          {bothMethods && (
            <button onClick={onClose} disabled={paying} data-testid="deposit-later"
              className="w-full h-11 sm:h-9 border border-border rounded-sm text-sm font-medium hover:bg-muted transition-colors mt-1">{tr("Plus tard", "Later")}</button>
          )}
        </div>

        <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground mt-4">
          <ShieldCheck className="w-3.5 h-3.5" /> {tr("Paiement sécurisé", "Secure payment")}
          {hasStripe && " · Stripe"}
          {hasStripe && hasPaypal && " &"}
          {hasPaypal && " PayPal"}
        </p>
      </div>
    </div>
  );
}

const annualLabel = (tr, v) => (v === "tv" ? tr("La TV à l'année", "TV billed yearly")
  : v === "all" ? tr("Toute l'offre à l'année", "Everything billed yearly") : tr("Au mois", "Monthly"));

/**
 * « Simplifiez-vous la vie : payez à l'année » (offres avec TV) : préférence du client, transmise avec sa signature ;
 * les prix restent mensuels. Après signature : le choix enregistré, en lecture seule.
 */
function AnnualPaymentChoice({ value, onChange, chosen, premium }) {
  const tr = useTr();
  const box = premium ? "rounded-[20px] bg-white border-2 border-primary/40 px-5 py-5 sm:px-8 sm:py-6" : "rounded-sm border-2 border-primary/40 bg-primary/5 px-5 py-5 sm:px-6";
  if (chosen) {
    return (
      <div className={box} data-testid="annual-payment-chosen">
        <p className="text-sm font-semibold">{tr("Paiement souhaité", "Preferred payment")} : {annualLabel(tr, chosen)}</p>
      </div>
    );
  }
  const options = [[null, annualLabel(tr, null)], ["tv", annualLabel(tr, "tv")], ["all", annualLabel(tr, "all")]];
  return (
    <div className={box} data-testid="annual-payment">
      <p className={premium ? "font-display text-xl font-bold" : "font-heading text-lg font-semibold"}>
        {tr("Simplifiez-vous la vie : payez à l'année", "Make life simple: pay once a year")}
      </p>
      <p className="text-sm text-muted-foreground mt-1">
        {tr("Un seul règlement pour 12 mois, aux mêmes tarifs. Votre choix est transmis avec votre signature.",
          "One payment for 12 months, at the same rates. Your choice is sent with your signature.")}
      </p>
      <div role="radiogroup" aria-label={tr("Paiement", "Payment")} className="grid gap-2 sm:grid-cols-3 mt-4">
        {options.map(([v, label]) => {
          const on = value === v;
          return (
            <button key={v || "month"} type="button" role="radio" aria-checked={on} onClick={() => onChange(v)} data-testid={`annual-payment-${v || "none"}`}
              className={`flex items-center gap-2 px-4 min-h-12 py-1.5 text-left text-sm font-semibold leading-snug border-2 transition-colors ${premium ? "rounded-xl" : "rounded-sm"} ${on
                ? "border-primary bg-primary text-white" : "border-border bg-background hover:border-primary/60"}`}>
              <span className={`w-4 h-4 rounded-full border-2 shrink-0 ${on ? "border-white bg-white/30" : "border-muted-foreground/50"}`} />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Page signée : rappel de la ligne téléphonique choisie (portabilité demandée ou nouveau numéro à attribuer). */
function PhoneLineNotice({ line, premium }) {
  const tr = useTr();
  const box = premium ? "rounded-[20px] bg-white border-2 border-primary/40 px-5 py-5 sm:px-8 sm:py-6" : "rounded-sm border-2 border-primary/40 bg-primary/5 px-5 py-5 sm:px-6";
  return (
    <div className={box} data-testid="phone-line-notice">
      <p className="text-sm font-semibold">
        {line.mode === "portabilite"
          ? tr(`Ligne téléphonique : portabilité de votre numéro ${line.number_display} demandée`, `Telephone line: porting of your number ${line.number_display} requested`)
          : tr("Ligne téléphonique : un nouveau numéro vous sera attribué", "Telephone line: a new number will be assigned to you")}
      </p>
    </div>
  );
}

function SignatureDialog({ onClose, onSign, deciding, brandName, terms, providers = [], legal, premium = false, publicId, stepCount = 1, signStep = 1, amounts = "",
  requiredDocuments = [], documents = [], onDocsUploaded, fibreRequired = false, fibreNumber = null, annualPayment = null, phoneLine = null }) {
  const tr = useTr();
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);
  const [name, setName] = useState("");
  // Offre fibre : numéro saisi par le partenaire (affiché), sinon saisi ici — obligatoire pour signer (le serveur refuse sinon).
  const [fibre, setFibre] = useState("");
  const askFibre = fibreRequired && !fibreNumber;
  // Ligne téléphonique en portabilité : mandat à cocher (le serveur refuse sinon). Nouveau numéro : rien à cocher.
  // On retient le TEXTE coché, pas un booléen : si l'offre change pendant la lecture (409 puis rechargement), la case se décoche seule.
  const porting = phoneLine?.mode === "portabilite";
  const [phoneMandateText, setPhoneMandateText] = useState("");
  const phoneMandate = porting && !!phoneLine.mandate_text && phoneMandateText === phoneLine.mandate_text;
  const [consent, setConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Prestataires tiers (ex. NEXTforTV) : une case par prestataire, non pré-cochée, obligatoire.
  const [providersAccepted, setProvidersAccepted] = useState(() => new Set());
  const toggleProvider = (id) => setProvidersAccepted((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const missingProvider = providers.find((pr) => !providersAccepted.has(pr.id));
  // Pièces justificatives : obligatoires pour signer (le serveur refuse sinon) — déposées ici, sur la page ou par le partenaire.
  const missingDocs = requiredDocuments.filter((rd) => !documents.some((d) => d.type === rd.type));
  const [expressStart, setExpressStart] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Haute résolution sur écrans Retina / mobile
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0A0A0A";

    const getPos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const start = (e) => { e.preventDefault(); drawing.current = true; const { x, y } = getPos(e); ctx.beginPath(); ctx.moveTo(x, y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const { x, y } = getPos(e); ctx.lineTo(x, y); ctx.stroke(); hasDrawn.current = true; };
    const end = () => { drawing.current = false; };

    canvas.addEventListener("mousedown", start);
    canvas.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", end);
    return () => {
      canvas.removeEventListener("mousedown", start);
      canvas.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      canvas.removeEventListener("touchstart", start);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", end);
    };
  }, []);

  const clear = () => {
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const r = canvas.getBoundingClientRect();
    canvas.getContext("2d").clearRect(0, 0, r.width * dpr, r.height * dpr);
    hasDrawn.current = false;
  };

  const submit = async () => {
    setError("");
    if (missingDocs.length) return setError(tr(`Déposez d'abord vos pièces : ${missingDocs.map((d) => d.label).join(", ")}.`, `Please upload your documents first: ${missingDocs.map((d) => d.label).join(", ")}.`));
    if (askFibre && !fibre.trim()) return setError(tr("Indiquez votre numéro de fibre pour signer.", "Please enter your fibre line number to sign."));
    if (porting && !phoneMandate) return setError(tr("Cochez le mandat de portabilité pour signer.", "Please tick the number porting authorisation to sign."));
    if (!name.trim()) return setError(tr("Indiquez votre nom complet.", "Please enter your full name."));
    if (!hasDrawn.current) return setError(tr("Veuillez dessiner votre signature.", "Please draw your signature."));
    if (!consent) return setError(tr("Vous devez accepter la valeur juridique de la signature.", "You must accept the legal value of the signature."));
    if (terms && !termsAccepted) return setError(tr("Vous devez accepter les conditions générales de vente.", "You must accept the general terms and conditions of sale."));
    if (missingProvider) return setError(tr(`Vous devez accepter les conditions de ${missingProvider.name}.`, `You must accept the ${missingProvider.name} terms.`));
    const r = await onSign(name.trim(), canvasRef.current.toDataURL("image/png"), {
      termsAccepted, expressStart, providersAccepted: [...providersAccepted], ...(askFibre ? { fibreNumber: fibre.trim() } : {}),
      ...(porting ? { phoneMandate: true } : {}),
    });
    if (r && r.ok === false) setError(r.message || tr("Une erreur est survenue.", "An error occurred."));
  };

  // Case CGV distincte, non pré-cochée et obligatoire (cf. plan D8) : bloque l'envoi tant
  // qu'elle n'est pas cochée, en plus de la validation dans submit().
  const termsBlocked = (!!terms && !termsAccepted) || !!missingProvider || missingDocs.length > 0 || (askFibre && !fibre.trim()) || (porting && !phoneMandate);
  const docsBlock = requiredDocuments.length > 0 && (
    <DocumentsBlock variant="sheet" publicId={publicId} requiredDocuments={requiredDocuments} documents={documents} onUploaded={onDocsUploaded} />
  );
  const annualLine = annualPayment && (
    <p className={premium ? "text-[13px]" : "text-sm mb-4"} data-testid="signature-annual">
      {tr("Paiement souhaité", "Preferred payment")} : <strong>{annualLabel(tr, annualPayment)}</strong>
    </p>
  );
  const fibreField = fibreRequired && (fibreNumber ? (
    <p className={premium ? "text-[13px]" : "text-sm mb-4"} data-testid="signature-fibre-known">
      {tr("Numéro de fibre", "Fibre line number")} : <strong>{fibreNumber}</strong>
    </p>
  ) : (
    <div className={premium ? "" : "mb-4"}>
      <label htmlFor="sig-fibre" className={premium ? "text-[13px] font-semibold" : "text-sm font-medium"}>
        {tr("Numéro de fibre", "Fibre line number")} <span className="font-normal text-muted-foreground">{tr("— obligatoire pour signer", "— required to sign")}</span>
      </label>
      <input id="sig-fibre" value={fibre} maxLength={100} onChange={(e) => setFibre(e.target.value)} data-testid="signature-fibre-number"
        placeholder={tr("Votre numéro de fibre", "Your fibre line number")}
        className={premium
          ? "mt-1.5 w-full h-12 px-3.5 rounded-xl border-[1.5px] border-[#DAD5CC] bg-white text-base outline-none focus:border-primary"
          : "w-full mt-1.5 h-11 px-3 border border-border rounded-sm outline-none focus:border-primary"} />
    </div>
  ));
  // Mandat : `whitespace-pre-wrap` garde les retours à la ligne du texte composé par le serveur (client anglophone : traduction, puis version française qui fait foi).
  const phoneField = phoneLine && (porting ? (
    <div className={premium ? "space-y-2" : "mb-4 space-y-2"} data-testid="signature-phone">
      <p className={premium ? "text-[13px]" : "text-sm"}>
        {tr("Ligne téléphonique : portabilité de votre numéro", "Telephone line: porting of your number")} <strong>{phoneLine.number_display}</strong>
      </p>
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" checked={phoneMandate} onChange={(e) => setPhoneMandateText(e.target.checked ? phoneLine.mandate_text : "")} data-testid="signature-phone-mandate"
          className={premium ? "w-[22px] h-[22px] mt-0.5 shrink-0" : "mt-0.5 w-5 h-5 shrink-0"} style={{ accentColor: "hsl(var(--primary))" }} />
        <span className={premium ? "text-[13px] leading-relaxed whitespace-pre-wrap" : "text-[13px] sm:text-xs text-muted-foreground whitespace-pre-wrap"}>{phoneLine.mandate_text}</span>
      </label>
    </div>
  ) : (
    <p className={premium ? "text-[13px]" : "text-sm mb-4"} data-testid="signature-phone-new">
      {tr("Ligne téléphonique : un nouveau numéro vous sera attribué après la signature.", "Telephone line: a new number will be assigned to you after signing.")}
    </p>
  ));

  if (premium) {
    // Feuille « conciergerie » : étape 1 du parcours, conditions d'abord (avec leurs documents), puis nom et
    // signature, puis l'engagement. Mêmes états, mêmes validations et mêmes data-testid que la modale classique.
    const box = "w-[22px] h-[22px] mt-0.5 shrink-0";
    const accent = { accentColor: "hsl(var(--primary))" };
    const docLink = "h-10 sm:h-8 px-2.5 rounded-lg bg-[#F4F1EC] text-[12.5px] font-bold flex items-center hover:bg-[#EAE5DC] transition-colors";
    const termsUrl = terms?.format === "pdf" ? fileUrl(terms.file_id) : `${API}/public/proposals/${publicId}/pdf`;
    return (
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-4" data-testid="signature-dialog"
        role="dialog" aria-modal="true" aria-labelledby="signature-title">
        <div className="h-full sm:h-auto sm:max-h-[92vh] w-full sm:max-w-lg overflow-y-auto bg-[#ECE9E3] sm:rounded-[22px] animate-fade-up">
          <div className="sticky top-0 z-10 bg-[#ECE9E3]/95 backdrop-blur px-5 pt-2 pb-3">
            <div className="flex items-center justify-between">
              <button type="button" onClick={onClose} disabled={deciding} aria-label={tr("Retour à la proposition", "Back to the proposal")} data-testid="signature-back"
                className="w-11 h-11 -ml-2.5 rounded-xl flex items-center justify-center hover:bg-black/5 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <span className="text-[13px] font-bold text-muted-foreground">
                {requiredDocuments.length
                  ? tr(`Étapes ${signStep - 1} et ${signStep} sur ${stepCount} · Pièces et signature`, `Steps ${signStep - 1} and ${signStep} of ${stepCount} · Documents and signature`)
                  : tr(`Étape ${signStep} sur ${stepCount} · Signature`, `Step ${signStep} of ${stepCount} · Signature`)}
              </span>
              <span className="w-8" aria-hidden="true" />
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-[#DAD5CC]">
              <div className="h-full rounded-full bg-primary" style={{ width: `${(100 * signStep) / stepCount}%` }} />
            </div>
          </div>

          <div className="px-5 pb-8 space-y-3.5">
            <div className="pt-1">
              <h2 id="signature-title" className="font-display font-extrabold text-[1.75rem] leading-[1.05] tracking-[-0.02em]">{tr("Signer la proposition", "Sign the proposal")}</h2>
              {amounts && <p className="text-sm text-muted-foreground mt-1.5">{amounts}</p>}
            </div>

            {docsBlock && (
              <div className="rounded-[18px] bg-white">
                <p className="px-4 pt-4 text-[12px] font-bold uppercase tracking-[0.06em] text-muted-foreground">{tr("Vos pièces", "Your documents")}</p>
                <p className="px-4 pt-0.5 pb-1 text-[12.5px] text-muted-foreground">{tr("Obligatoires pour signer · photo ou PDF, 10 Mo max", "Required to sign · photo or PDF, 10 MB max")}</p>
                {docsBlock}
              </div>
            )}

            {(terms || providers.length > 0 || legal?.consumer_like) && (
              <div className="rounded-[18px] bg-white">
                <p className="px-4 pt-4 pb-1 text-[12px] font-bold uppercase tracking-[0.06em] text-muted-foreground">{tr("Vos conditions", "Your terms")}</p>
                {terms && (
                  <div className="flex gap-3 items-start px-4 py-3" data-testid="signature-terms-wrapper">
                    <input id="sig-terms" type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
                      data-testid="signature-terms-consent" className={box} style={accent} />
                    <label htmlFor="sig-terms" className="flex-1 text-sm leading-relaxed cursor-pointer">
                      {tr("J'ai lu et j'accepte les ", "I have read and accept the ")}<strong className="font-bold">{tr("conditions générales de vente", "general terms and conditions of sale")}</strong>
                      {brandName ? tr(` de ${brandName}`, ` of ${brandName}`) : ""} (version {terms.version_label}{tr("", ", French version prevailing")}).
                    </label>
                    <a href={termsUrl} target="_blank" rel="noopener noreferrer" className={`shrink-0 ${docLink}`} data-testid="signature-terms-read">{tr("Lire", "Read")}</a>
                  </div>
                )}
                {providers.map((pr) => (
                  <div key={pr.id} className="flex gap-3 items-start px-4 py-3 border-t border-[#EFEBE4]" data-testid={`signature-provider-${pr.id}`}>
                    <input id={`sig-provider-${pr.id}`} type="checkbox" checked={providersAccepted.has(pr.id)} onChange={() => toggleProvider(pr.id)}
                      data-testid={`signature-provider-consent-${pr.id}`} className={box} style={accent} />
                    <label htmlFor={`sig-provider-${pr.id}`} className="flex-1 text-sm leading-relaxed cursor-pointer">
                      {tr("J'ai lu et j'accepte les", "I have read and accept the")}{" "}
                      <strong className="font-bold">{tr(
                        `${pr.documents.length ? pr.documents.map((d) => d.label).join(" et ") : "conditions"} de ${pr.name}`,
                        `${pr.name} ${pr.documents.length ? pr.documents.map((d) => d.label).join(" and ") : "terms"}`)}</strong>,
                      {" "}{tr("qui assure :", "who provides:")} {pr.lines.join(", ")}.
                    </label>
                    {pr.documents.length > 0 && (
                      <div className="shrink-0 flex flex-col gap-1.5">
                        {pr.documents.map((d) => providerDocHref(d) && (
                          <a key={d.file_id || d.url} href={providerDocHref(d)} target="_blank" rel="noopener noreferrer" className={docLink}>{d.label}</a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {legal?.consumer_like && (
                  <div className="mx-3 mb-3 mt-1 p-3 rounded-xl bg-[#F6F4F0] space-y-2.5" data-testid="signature-withdrawal-notice">
                    <p className="text-[13px] leading-relaxed">{tr(`Délai de rétractation : ${legal.withdrawal_days || 14} jours à compter de la signature.`, `Withdrawal period: ${legal.withdrawal_days || 14} days from signature.`)}</p>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={expressStart} onChange={(e) => setExpressStart(e.target.checked)}
                        data-testid="signature-express-start" className={box} style={accent} />
                      <span className="text-[13px] leading-relaxed">{tr("Je demande que le service commence avant la fin du délai de rétractation.", "I request that the service begins before the end of the withdrawal period.")}</span>
                    </label>
                  </div>
                )}
              </div>
            )}

            <div className="rounded-[18px] bg-white p-4 space-y-3">
              <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-muted-foreground">{tr("Votre signature", "Your signature")}</p>
              {annualLine}
              {fibreField}
              {phoneField}
              <div>
                <label htmlFor="sig-name" className="text-[13px] font-semibold">{tr("Nom complet", "Full name")}</label>
                <input id="sig-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="signature-name" autoComplete="name"
                  placeholder={tr("Prénom Nom", "First name Last name")} className="mt-1.5 w-full h-12 px-3.5 rounded-xl border-[1.5px] border-[#DAD5CC] bg-white text-base outline-none focus:border-primary" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-semibold">{tr("Signez avec le doigt", "Sign with your finger")}</span>
                <button type="button" onClick={clear} data-testid="signature-clear"
                  className="h-9 px-3 rounded-lg bg-[#F4F1EC] text-[12.5px] font-bold flex items-center gap-1.5 hover:bg-[#EAE5DC] transition-colors">
                  <Eraser className="w-3.5 h-3.5" /> {tr("Effacer", "Clear")}
                </button>
              </div>
              <canvas ref={canvasRef} width={400} height={160} data-testid="signature-canvas" aria-label={tr("Zone de signature", "Signature area")}
                className="w-full h-40 rounded-[14px] border-[1.5px] border-dashed border-[#CFC9BE] bg-[#FBFAF7] touch-none cursor-crosshair" />
            </div>

            <label className="flex items-start gap-3 px-1 cursor-pointer">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="signature-consent" className={box} style={accent} />
              <span className="text-[13.5px] leading-relaxed">{tr("J'accepte cette proposition et reconnais que ma signature électronique a valeur d'engagement. Un exemplaire signé (PDF) vous est envoyé par email.", "I accept this proposal and acknowledge that my electronic signature is legally binding. A signed copy (PDF) will be sent to you by email.")}</span>
            </label>

            {error && <p className="text-sm font-medium text-rose-700 px-1" data-testid="signature-error">{error}</p>}

            <button onClick={submit} disabled={deciding || termsBlocked} data-testid="signature-submit"
              className="w-full h-14 rounded-[14px] bg-primary text-white text-[16.5px] font-bold flex items-center justify-center gap-2.5 hover:brightness-110 transition disabled:opacity-50 disabled:cursor-not-allowed">
              {deciding ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" strokeWidth={2.5} />} {tr("Confirmer la signature", "Confirm signature")}
            </button>
            <button type="button" onClick={onClose} disabled={deciding} data-testid="signature-cancel"
              className="w-full h-11 rounded-xl text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
              {tr("Annuler", "Cancel")}
            </button>
            <p className="text-[11.5px] text-muted-foreground leading-relaxed px-1">
              {tr("En signant, vos nom, signature et adresse IP sont enregistrés comme preuve.", "When you sign, your name, signature and IP address are recorded as proof.")}{" "}
              <PrivacyNotice companyName={brandName} testIdSuffix="-signature" className="underline decoration-dotted underline-offset-2 hover:text-foreground transition-colors" />
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Téléphone : plein écran (plus de place pour signer du doigt), fermeture en haut ; écran large : modale centrée.
  const check = "mt-0.5 w-5 h-5 shrink-0";
  return (
    <div className="fixed inset-0 z-50 sm:flex sm:items-center sm:justify-center bg-black/50 backdrop-blur-sm sm:p-4" data-testid="signature-dialog"
      role="dialog" aria-modal="true" aria-labelledby="signature-title">
      <div className="bg-white sm:rounded-sm shadow-2xl w-full sm:max-w-md h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto px-5 pt-3 pb-8 sm:p-6 animate-fade-up">
        <div className="flex items-center gap-2 mb-1">
          <PenLine className="w-5 h-5 text-primary shrink-0" />
          <h2 id="signature-title" className="font-heading text-xl font-bold flex-1">{tr("Signer la proposition", "Sign the proposal")}</h2>
          <button type="button" onClick={onClose} disabled={deciding} aria-label={tr("Fermer", "Close")} data-testid="signature-close"
            className="w-11 h-11 -mr-2.5 shrink-0 flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{tr("Votre signature électronique vaut acceptation de la proposition.", "Your electronic signature constitutes acceptance of the proposal.")}</p>

        {docsBlock && (
          <div className="mb-4">
            <p className="text-sm font-medium">{tr("Vos pièces", "Your documents")} <span className="font-normal text-muted-foreground">{tr("— obligatoires pour signer", "— required to sign")}</span></p>
            <div className="mt-1.5 border border-border rounded-sm">{docsBlock}</div>
          </div>
        )}

        {annualLine}
        {fibreField}
        {phoneField}
        <label className="text-sm font-medium">{tr("Nom complet", "Full name")}</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="signature-name"
          placeholder={tr("Prénom Nom", "First name Last name")} className="w-full mt-1.5 mb-4 h-11 px-3 border border-border rounded-sm outline-none focus:border-primary" />

        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-medium">{tr("Votre signature", "Your signature")}</label>
          <button onClick={clear} data-testid="signature-clear" className="flex items-center gap-1 h-10 -my-2 px-2 -mr-2 text-xs text-muted-foreground hover:text-foreground">
            <Eraser className="w-3.5 h-3.5" /> {tr("Effacer", "Clear")}
          </button>
        </div>
        <canvas ref={canvasRef} width={400} height={160} data-testid="signature-canvas"
          className="w-full h-40 border-2 border-dashed border-border rounded-sm bg-surface touch-none cursor-crosshair" />

        {terms && (
          <label className="flex items-start gap-2.5 mt-4 cursor-pointer" data-testid="signature-terms-wrapper">
            <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
              data-testid="signature-terms-consent" className={check} style={{ accentColor: "hsl(var(--primary))" }} />
            <span className="text-[13px] sm:text-xs text-muted-foreground">
              {tr("J'ai lu et j'accepte les ", "I have read and accept the ")}<span className="font-medium text-foreground">{tr("conditions générales de vente", "general terms and conditions of sale")} (version {terms.version_label}{tr("", ", French version prevailing")})</span>.
            </span>
          </label>
        )}

        {providers.map((pr) => (
          <label key={pr.id} className="flex items-start gap-2.5 mt-3 cursor-pointer" data-testid={`signature-provider-${pr.id}`}>
            <input type="checkbox" checked={providersAccepted.has(pr.id)} onChange={() => toggleProvider(pr.id)}
              data-testid={`signature-provider-consent-${pr.id}`} className={check} style={{ accentColor: "hsl(var(--primary))" }} />
            <span className="text-[13px] sm:text-xs text-muted-foreground">
              {tr("J'ai lu et j'accepte les", "I have read and accept the")}{" "}
              <span className="font-medium text-foreground">{tr(
                `${pr.documents.length ? pr.documents.map((d) => d.label).join(" et ") : "conditions"} de ${pr.name}`,
                `${pr.name} ${pr.documents.length ? pr.documents.map((d) => d.label).join(" and ") : "terms"}`)}</span>,
              {" "}{tr("qui assure :", "who provides:")} {pr.lines.join(", ")}.
            </span>
          </label>
        ))}

        {legal?.consumer_like && (
          <div className="mt-3 p-3 rounded-sm bg-amber-50 border border-amber-200 space-y-2" data-testid="signature-withdrawal-notice">
            <p className="text-[13px] sm:text-xs text-amber-800">
              {tr(`Vous disposez d'un délai de rétractation de ${legal.withdrawal_days || 14} jours à compter de la signature.`, `You have a ${legal.withdrawal_days || 14}-day withdrawal period from signature.`)}
            </p>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={expressStart} onChange={(e) => setExpressStart(e.target.checked)}
                data-testid="signature-express-start" className={check} style={{ accentColor: "hsl(var(--primary))" }} />
              <span className="text-[13px] sm:text-xs text-amber-800">{tr("Je demande que le service commence avant la fin du délai de rétractation.", "I request that the service begins before the end of the withdrawal period.")}</span>
            </label>
          </div>
        )}

        <label className="flex items-start gap-2.5 mt-4 cursor-pointer">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="signature-consent" className={check} style={{ accentColor: "hsl(var(--primary))" }} />
          <span className="text-[13px] sm:text-xs text-muted-foreground">{tr("J'accepte cette proposition et reconnais que ma signature électronique a valeur d'engagement. Un exemplaire signé (PDF) vous est envoyé par email.", "I accept this proposal and acknowledge that my electronic signature is legally binding. A signed copy (PDF) will be sent to you by email.")}</span>
        </label>

        {error && <p className="text-[13px] sm:text-xs text-rose-600 mt-3" data-testid="signature-error">{error}</p>}

        <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
          {tr("En signant, vos nom, signature et adresse IP sont enregistrés comme preuve.", "When you sign, your name, signature and IP address are recorded as proof.")}{" "}
          <PrivacyNotice companyName={brandName} testIdSuffix="-signature" className="underline decoration-dotted underline-offset-2 hover:text-foreground transition-colors" />
        </p>

        {/* Mobile : boutons empilés (côte à côte, « Confirmer la signature » passait sur 2 lignes tronquées). */}
        <div className="flex flex-col-reverse sm:flex-row gap-2 mt-5">
          <button onClick={onClose} disabled={deciding} data-testid="signature-cancel"
            className="sm:flex-1 h-11 border border-border rounded-sm font-medium hover:bg-muted transition-colors">{tr("Annuler", "Cancel")}</button>
          <button onClick={submit} disabled={deciding || termsBlocked} data-testid="signature-submit"
            className="sm:flex-1 h-11 bg-primary text-white rounded-sm font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
            {deciding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {tr("Confirmer la signature", "Confirm signature")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * CGV présentées avant la signature : texte en whitespace-pre-wrap, HTML tiers dans une
 * iframe sandbox="" (jamais dangerouslySetInnerHTML — même patron que EmailEditor.jsx). Le
 * PDF public embarque déjà les CGV en annexe côté serveur ; le lien ici pointe vers ce même PDF.
 */
export function TermsBlock({ terms, translation, publicId, pdfHref }) {
  const tr = useTr();
  const lang = useLang();
  const isPdf = terms.format === "pdf";
  return (
    <div className="border-t border-black/5 px-5 sm:px-12 py-8" data-testid="terms-block">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <SectionHeading>{tr("Conditions générales de vente", "General terms and conditions of sale")}</SectionHeading>
        <span className="text-xs text-muted-foreground shrink-0 mt-1" data-testid="terms-version">version {terms.version_label}</span>
      </div>
      {/* Client anglophone : traduction pour information, puis la version française, seule acceptée et qui fait foi. */}
      {translation && (
        <>
          <p className="text-xs text-muted-foreground mb-2" data-testid="terms-translation-notice">
            English translation provided for convenience. In case of discrepancy, the French version below prevails.
          </p>
          <TermsDocument doc={translation} suffix="-en" />
          <p className="text-sm font-semibold mt-6 mb-2" lang="fr">{tr("Version française (fait foi)", "French version (governing)")}</p>
        </>
      )}
      {lang === "en" && !translation && (
        <p className="text-xs text-muted-foreground mb-2" data-testid="terms-french-only">These terms and conditions are available in French only.</p>
      )}
      <TermsDocument doc={terms} />
      <a href={pdfHref || `${API}/public/proposals/${publicId}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="terms-download-pdf"
        className="flex w-fit items-center gap-1.5 min-h-11 text-xs font-semibold text-primary hover:underline mt-1">
        <Download className="w-3.5 h-3.5" /> {isPdf ? tr("Télécharger la proposition (PDF)", "Download the proposal (PDF)") : tr("Télécharger le PDF (proposition + CGV)", "Download the PDF (proposal + terms)")}
      </a>
    </div>
  );
}

/** Un document de CGV : PDF distinct (lecteur du navigateur), HTML en iframe sandbox="" (aucun script) ou texte brut. */
export function TermsDocument({ doc, suffix = "" }) {
  const tr = useTr();
  if (doc.format === "pdf") {
    // CGV publiées en PDF : document distinct, ouvert dans le lecteur du navigateur (aussi joint à l'exemplaire signé).
    return (
      <a href={fileUrl(doc.file_id)} target="_blank" rel="noopener noreferrer" data-testid={`terms-open-pdf${suffix}`}
        className="inline-flex items-center gap-2 border border-border rounded-sm px-4 py-3 text-sm font-medium hover:border-primary transition-colors">
        <FileText className="w-4 h-4 text-primary" /> {tr(`Lire les ${doc.title || "conditions générales de vente"} (PDF)`, `Read the ${doc.title || "general terms and conditions of sale"} (PDF)`)}
      </a>
    );
  }
  return (
    <div className="border border-border rounded-sm bg-surface overflow-hidden" data-testid={`terms-content${suffix}`}>
      {doc.format === "html" ? (
        <iframe title={doc.title || tr("Conditions générales de vente", "General terms and conditions of sale")} data-testid={`terms-iframe${suffix}`} srcDoc={doc.content} sandbox=""
          className="w-full h-72 bg-white border-0" />
      ) : (
        <div className="max-h-72 overflow-y-auto p-4">
          <p className="text-sm whitespace-pre-wrap text-foreground/80 leading-relaxed">{doc.content}</p>
        </div>
      )}
    </div>
  );
}

/** Prestataires tiers de l'offre (ex. NEXTforTV) : services qu'ils assurent, mention, documents à lire avant de signer. */
export function ProvidersBlock({ providers }) {
  const tr = useTr();
  return (
    <div className="border-t border-black/5 px-5 sm:px-12 py-8 space-y-6" data-testid="providers-block">
      {providers.map((pr) => (
        <div key={pr.id} data-testid={`provider-${pr.id}`}>
          <SectionHeading>{tr(`Service assuré par ${pr.name}`, `Service provided by ${pr.name}`)}</SectionHeading>
          <p className="text-sm text-foreground/80">{pr.lines.join(", ")}.</p>
          {pr.note && <p className="text-sm text-muted-foreground mt-1" data-testid={`provider-note-${pr.id}`}>{pr.note}</p>}
          {pr.documents.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {/* PDF (joint à l'exemplaire signé) ou document en ligne (lien https, ouvert dans un nouvel onglet). */}
              {pr.documents.map((d) => providerDocHref(d) && (
                <a key={d.file_id || d.url} href={providerDocHref(d)} target="_blank" rel="noopener noreferrer" data-testid={`provider-doc-${d.file_id || d.label}`}
                  className="inline-flex items-center gap-1.5 border border-border rounded-sm px-3 py-2 max-sm:min-h-11 text-xs font-medium hover:border-primary transition-colors">
                  {d.url ? <ExternalLink className="w-3.5 h-3.5 text-primary" /> : <FileText className="w-3.5 h-3.5 text-primary" />}
                  {d.label} {pr.name} {d.url ? tr("(en ligne)", "(online)") : "(PDF)"}
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Rappel du délai de rétractation (régime hors établissement, particulier/tpe) + accès au
 * formulaire type. Purement informatif ici — la case facultative d'exécution anticipée vit
 * dans la modale de signature (SignatureDialog), au moment même de la décision.
 */
export function WithdrawalBlock({ legal, onOpenForm }) {
  const tr = useTr();
  const days = legal.withdrawal_days || 14;
  return (
    <div className="border-t border-black/5 px-5 sm:px-12 py-8" data-testid="withdrawal-block">
      <SectionHeading>{tr(`Droit de rétractation (${days} jours)`, `Right of withdrawal (${days} days)`)}</SectionHeading>
      <p className="text-sm text-foreground/80 leading-relaxed mb-4">
        {tr(`Vous disposez d'un délai de ${days} jours à compter de la signature pour vous rétracter, sans avoir à justifier de motifs ni à supporter de pénalités.`,
          `You have ${days} days from signature to withdraw, without giving any reason and without penalty.`)}
      </p>
      <button onClick={onOpenForm} data-testid="withdrawal-open"
        className="inline-flex items-center gap-1.5 h-11 sm:h-9 px-4 rounded-sm border border-border text-sm font-medium hover:bg-muted transition-colors">
        {tr("Voir le formulaire type de rétractation", "View the model withdrawal form")}
      </button>
    </div>
  );
}

export function WithdrawalFormDialog({ text, onClose }) {
  const tr = useTr();
  const title = tr("Formulaire type de rétractation", "Model withdrawal form");
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch (e) { /* presse-papiers indisponible */ }
  };
  // Imprime le formulaire seul (window.print() imprimerait toute la proposition sous la modale).
  const print = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.title = title;
    const pre = w.document.createElement("pre");
    pre.style.cssText = "white-space:pre-wrap;font:14px/1.6 sans-serif;padding:32px";
    pre.textContent = text;
    w.document.body.appendChild(pre);
    w.print();
  };
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="withdrawal-dialog"
      role="dialog" aria-modal="true" aria-labelledby="withdrawal-dialog-title">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 sm:p-6 animate-fade-up">
        <h2 id="withdrawal-dialog-title" className="font-heading text-lg font-bold mb-4">{title}</h2>
        <div className="max-h-[45vh] sm:max-h-80 overflow-y-auto border border-border rounded-sm bg-surface p-4 mb-4">
          <p className="text-sm whitespace-pre-wrap text-foreground/80 leading-relaxed" data-testid="withdrawal-form-text">{text}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={copy} data-testid="withdrawal-copy" className="flex-1 h-11 sm:h-10 border border-border rounded-sm text-sm font-medium hover:bg-muted transition-colors">{tr("Copier", "Copy")}</button>
          <button onClick={print} data-testid="withdrawal-print" className="flex-1 h-11 sm:h-10 border border-border rounded-sm text-sm font-medium hover:bg-muted transition-colors">{tr("Imprimer", "Print")}</button>
          <button onClick={onClose} data-testid="withdrawal-close" className="flex-1 h-11 sm:h-10 bg-primary text-white rounded-sm text-sm font-semibold hover:bg-primary/90 transition-colors">{tr("Fermer", "Close")}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Dépôt des pièces justificatives par le client (lien public, sans compte) : jamais de lien de
 * téléchargement ici (cf. contrat API — la lecture est réservée à l'équipe/au partenaire).
 * État local (upload en cours, erreur) encapsulé dans le composant : pas besoin de le faire
 * remonter dans PublicProposal, seul `onUploaded` (rechargement de la proposition) est partagé.
 */
/** `endpoint` : route de dépôt (défaut : celle de la proposition) — la page d'un dossier groupé dépose UN fichier pour toutes les villas. */
export function DocumentsBlock({ publicId, requiredDocuments, documents, onUploaded, signed = false, variant = "page", endpoint }) {
  const tr = useTr();
  const lang = useLang();
  const [uploadingType, setUploadingType] = useState(null);
  const [error, setError] = useState("");

  const upload = async (type, file) => {
    if (!file) return;
    setError("");
    setUploadingType(type);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type", type);
      await api.post(endpoint || `/public/proposals/${publicId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onUploaded();
    } catch (e) {
      setError(formatAxiosError(e));
    } finally {
      setUploadingType(null);
    }
  };

  // Feuille de signature : liste compacte (déposée ✓ / « Déposer »), photo possible depuis le téléphone.
  if (variant === "sheet") {
    return (
      <div data-testid="signature-documents">
        <ul className="divide-y divide-border">
          {(requiredDocuments || []).map((rd) => {
            const existing = (documents || []).find((d) => d.type === rd.type);
            const busy = uploadingType === rd.type;
            return (
              <li key={rd.type} className="flex items-center gap-3 px-4 py-3" data-testid={`sig-doc-row-${rd.type}`}>
                {existing
                  ? <span className="w-[22px] h-[22px] shrink-0 rounded-full bg-emerald-600 text-white flex items-center justify-center"><Check className="w-3.5 h-3.5" strokeWidth={3} /></span>
                  : <span className="w-[22px] h-[22px] shrink-0 rounded-full border-[1.5px] border-[#CFC9BE]" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{rd.label}</p>
                  <p className={`text-xs truncate ${existing ? "text-muted-foreground" : "text-amber-700"}`} data-testid={`sig-doc-status-${rd.type}`}>
                    {existing ? `${tr("Déposée", "Uploaded")} · ${existing.original_name || "document"}` : tr("À déposer", "To upload")}
                  </p>
                </div>
                {!existing && (
                  <label className="shrink-0">
                    <input type="file" accept="image/*,application/pdf" className="sr-only" data-testid={`sig-doc-input-${rd.type}`}
                      disabled={busy} onChange={(e) => { upload(rd.type, e.target.files?.[0]); e.target.value = ""; }} />
                    <span className={`inline-flex items-center gap-1.5 h-11 sm:h-9 px-3 rounded-lg bg-surface text-[12.5px] font-bold transition-colors ${busy ? "opacity-50" : "hover:brightness-95 cursor-pointer"}`}>
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {tr("Déposer", "Upload")}
                    </span>
                  </label>
                )}
              </li>
            );
          })}
        </ul>
        {error && <p className="text-xs text-rose-600 px-4 pb-3" data-testid="sig-doc-error">{error}</p>}
      </div>
    );
  }

  return (
    <div className="border-t border-black/5 px-5 sm:px-12 py-8" data-testid="documents-block">
      <SectionHeading>{tr("Pièces à fournir", "Documents to provide")}</SectionHeading>
      <p className="text-xs text-muted-foreground mb-4">
        {!signed && <strong className="font-semibold text-foreground">{tr("Obligatoires pour signer. ", "Required to sign. ")}</strong>}
        {tr("Formats acceptés : PDF, JPEG, PNG — 10 Mo maximum par fichier.", "Accepted formats: PDF, JPEG, PNG — 10 MB maximum per file.")}
      </p>
      <ul className="space-y-3">
        {(requiredDocuments || []).map((rd) => {
          const existing = (documents || []).find((d) => d.type === rd.type);
          const busy = uploadingType === rd.type;
          return (
            <li key={rd.type} className="flex flex-wrap items-center gap-3 p-3 rounded-sm border border-border" data-testid={`doc-row-${rd.type}`}>
              <span
                className={`text-xs font-semibold px-2 py-1 rounded-sm shrink-0 ${existing ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}
                data-testid={`doc-status-${rd.type}`}
              >
                {existing ? tr("Déposée", "Uploaded") : tr("Manquante", "Missing")}
              </span>
              <div className="flex-1 min-w-[160px]">
                <p className="text-sm font-medium">{rd.label}</p>
                {existing && (
                  <p className="text-xs text-muted-foreground" data-testid={`doc-info-${rd.type}`}>
                    {existing.original_name} · {new Date(existing.created_at).toLocaleDateString(dateLocale(lang))}
                  </p>
                )}
              </div>
              {!existing && (
                <label className="shrink-0">
                  <input type="file" accept="image/*,application/pdf" className="hidden" data-testid={`doc-input-${rd.type}`}
                    disabled={busy}
                    onChange={(e) => upload(rd.type, e.target.files?.[0])} />
                  <span className={`inline-flex items-center gap-1.5 h-11 sm:h-9 px-4 rounded-sm border border-border text-sm font-medium transition-colors ${busy ? "opacity-50" : "hover:bg-muted cursor-pointer"}`}>
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {tr("Déposer", "Upload")}
                  </span>
                </label>
              )}
            </li>
          );
        })}
      </ul>
      {error && <p className="text-xs text-rose-600 mt-3" data-testid="doc-error">{error}</p>}
    </div>
  );
}

/** Lignes TV de l'offre (instantané `channels` posé par le catalogue sur la ligne de grille). */
function tvLines(sections) {
  return (sections || []).filter((s) => s.type === "pricing").flatMap((s) => s.items || []).filter((it) => it.channels);
}

/** Niveau NEXT d'une chaîne (défaut « au choix » pour les line-ups sans cœur). */
const channelTier = (c) => c.tier || "choice";

/**
 * Offre TV : par formule, cœur inclus, cœur + N au choix, ou toutes ; aperçu des logos puis la grille complète à la
 * demande (classique : tuiles ; premium : incluses / à choisir).
 */

function ChannelsBlock({ channels, lines, premium = false, anchor = {} }) {
  const tr = useTr();
  const [all, setAll] = useState(false);
  // Chaînes de l'offre : le cœur (inclus partout) ; + les chaînes au choix si une formule en propose ; + les chaînes
  // réservées à All NEXT si l'offre comprend « toutes les chaînes ».
  const hasAll = lines.some((l) => l.channels.mode === "all");
  const hasChoice = hasAll || lines.some((l) => l.channels.mode === "choice");
  const offer = channels.filter((c) => channelTier(c) === "core" || (channelTier(c) === "all" ? hasAll : hasChoice));
  // Bientôt disponibles : dans la grille complète (badge) et rappelées par leur nom, mais hors comptes et hors aperçus.
  const available = channels.filter((c) => !c.coming_soon);
  const soon = offer.filter((c) => c.coming_soon);
  const coreCount = available.filter((c) => channelTier(c) === "core").length;
  const choiceCount = available.filter((c) => channelTier(c) === "choice").length;
  const soonLine = soon.length > 0 && (
    <p className={premium ? "mt-4 text-[12.5px] text-muted-foreground leading-snug" : "mt-3 text-xs text-muted-foreground"} data-testid="channels-coming-soon">
      <span className="font-semibold text-foreground">{tr("Bientôt disponibles", "Coming soon")}</span>{tr(" : ", ": ")}{soon.map((c) => c.name).join(", ")}
    </p>
  );
  const lineText = (l) => {
    if (l.channels.mode === "all") return tr(`toutes les chaînes ci-dessous sont incluses (${available.length}).`, `all the channels below are included (${available.length}).`);
    if (l.channels.mode === "core") return tr(`les ${coreCount} chaînes du cœur Start NEXT, incluses.`, `the ${coreCount} Start NEXT core channels, included.`);
    return coreCount
      ? tr(`les ${coreCount} chaînes du cœur incluses + ${l.channels.count} chaînes au choix parmi ${choiceCount}, à choisir après la signature.`,
        `the ${coreCount} core channels included + ${l.channels.count} channels of your choice out of ${choiceCount}, to choose after signing.`)
      : tr(`vous choisirez ${l.channels.count} chaînes parmi celles-ci après la signature.`, `you will choose ${l.channels.count} of these channels after signing.`);
  };
  const logos = offer.filter((c) => c.logo_file_id && !c.coming_soon);
  if (!premium) {
    const preview = all ? offer : logos.slice(0, 18);
    return (
      <div data-testid="channels-block">
        <SectionHeading>{tr("Vos chaînes TV", "Your TV channels")}</SectionHeading>
        <ul className="text-sm space-y-1.5 mb-5">
          {lines.map((l) => (
            <li key={l.id} className="flex items-start gap-2" data-testid={`channels-line-${l.id}`}>
              <Tv className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <span><strong>{l.name}</strong>{tr(" : ", ": ")}{lineText(l)}</span>
            </li>
          ))}
        </ul>
        <ChannelTiles channels={preview} />
        {soonLine}
        {offer.length > preview.length && (
          <button type="button" onClick={() => setAll(true)} data-testid="channels-show-all"
            className="mt-2 min-h-11 text-sm font-medium text-primary underline underline-offset-2">
            {tr(`Voir les ${offer.length} chaînes`, `See all ${offer.length} channels`)}
          </button>
        )}
      </div>
    );
  }
  // Premium : les chaînes incluses d'un côté, celles à choisir après la signature de l'autre (aperçu des logos ;
  // pointillés = à choisir), puis la grille complète à la demande.
  const choiceLines = lines.filter((l) => l.channels.mode === "choice");
  const included = hasAll ? offer.filter((c) => !c.coming_soon) : available.filter((c) => channelTier(c) === "core");
  const toChoose = !hasAll && choiceLines.length ? available.filter((c) => channelTier(c) === "choice") : [];
  // Aperçus sans logo en double : ABC Miami (incluse) et ABC New York (au choix) partagent le même logo.
  const uniqueLogos = (list, exclude = []) => {
    const seen = new Set(exclude);
    return list.filter((c) => {
      if (!c.logo_file_id || seen.has(c.logo_file_id)) return false;
      seen.add(c.logo_file_id);
      return true;
    });
  };
  const includedLogos = uniqueLogos(included).slice(0, 8);
  const chooseLogos = uniqueLogos(toChoose, included.map((c) => c.logo_file_id)).slice(0, 4);
  const chooseLabel = choiceLines.length === 1
    ? tr(`${choiceLines[0].channels.count} parmi ${toChoose.length}`, `${choiceLines[0].channels.count} out of ${toChoose.length}`)
    : tr(`parmi ${toChoose.length}`, `out of ${toChoose.length}`);
  return (
    <div {...anchor} data-testid="channels-block" className="scroll-mt-24 rounded-[20px] bg-white p-5 sm:p-8">
      <SectionHeading>{tr("Vos chaînes TV", "Your TV channels")}</SectionHeading>
      <ul className="space-y-2 text-[14.5px] leading-snug">
        {lines.map((l) => (
          <li key={l.id} className="flex items-start gap-2.5" data-testid={`channels-line-${l.id}`}>
            <Tv className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <span><strong className="font-semibold">{l.name}</strong>{tr(" : ", ": ")}{lineText(l)}</span>
          </li>
        ))}
      </ul>
      {all ? (
        <div className="mt-6"><ChannelTiles channels={offer} />{soonLine}</div>
      ) : (
        <>
          {included.length > 0 && (
            <div className="mt-6" data-testid="channels-included">
              <p className="text-[12.5px] font-bold">{tr(`Incluses · ${included.length} chaînes`, `Included · ${included.length} channels`)}</p>
              {includedLogos.length > 0 && <LogoPreview channels={includedLogos} />}
              {included.length > includedLogos.length && (
                <p className="mt-2 text-[12.5px] text-muted-foreground">+ {tr(`${included.length - includedLogos.length} autres chaînes incluses`, `${included.length - includedLogos.length} more channels included`)}</p>
              )}
            </div>
          )}
          {toChoose.length > 0 && (
            <div className="mt-5" data-testid="channels-to-choose">
              <p className="text-[12.5px] font-bold">{tr("À choisir après la signature", "To choose after signing")} · {chooseLabel}</p>
              {chooseLogos.length > 0 && <LogoPreview channels={chooseLogos} dashed />}
              {toChoose.length > chooseLogos.length && (
                <p className="mt-2 text-[12.5px] text-muted-foreground">{tr(`Et ${toChoose.length - chooseLogos.length} autres au choix`, `And ${toChoose.length - chooseLogos.length} more to choose from`)}</p>
              )}
            </div>
          )}
          {soonLine}
          <button type="button" onClick={() => setAll(true)} data-testid="channels-show-all"
            className="mt-6 h-11 px-5 rounded-xl bg-[#F4F1EC] text-sm font-bold hover:bg-[#EAE5DC] transition-colors">
            {tr(`Voir les ${offer.length} chaînes`, `See all ${offer.length} channels`)}
          </button>
        </>
      )}
    </div>
  );
}

/** Aperçu de logos (premium) : tuiles pleines pour les chaînes incluses, en pointillés pour celles à choisir. */
function LogoPreview({ channels, dashed = false }) {
  return (
    <div className="mt-2.5 grid grid-cols-4 sm:grid-cols-8 gap-1.5">
      {channels.map((c) => (
        <div key={c.id} className={`h-11 rounded-[10px] flex items-center justify-center p-2 ${dashed ? "border-[1.5px] border-dashed border-[#CFC9BE]" : "bg-[#F6F4F0]"}`}>
          <img src={fileUrl(c.logo_file_id)} alt={c.name} loading="lazy" className="max-w-full max-h-full object-contain" />
        </div>
      ))}
    </div>
  );
}

/** Choix des chaînes après signature : exactement N par formule « au choix », modifiable jusqu'au dossier complet. */
function ChannelPicker({ publicId, line, channels, coreCount = 0, initial, onSaved, premium = false }) {
  const tr = useTr();
  const chosen = Array.isArray(initial) ? initial : [];
  const [sel, setSel] = useState(() => new Set(chosen));
  const [open, setOpen] = useState(chosen.length !== line.count);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const full = sel.size >= line.count;
  const hasSoon = channels.some((c) => c.coming_soon);
  const soonHint = tr("Les chaînes marquées « Bientôt » ne se choisissent pas encore.", "Channels marked “Soon” can't be chosen yet.");
  const toggle = (id) => setSel((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else if (next.size < line.count) next.add(id);
    return next;
  });
  const save = async () => {
    setSaving(true); setError("");
    try {
      await api.post(`/public/proposals/${publicId}/channels`, { selections: { [line.item_id]: [...sel] } });
      setOpen(false);
      onSaved?.();
    } catch (e) {
      setError(formatAxiosError(e));
    } finally { setSaving(false); }
  };
  if (!open) {
    if (premium) {
      // Récapitulatif du choix enregistré : les logos des chaînes retenues, pour se relire d'un coup d'œil.
      const logos = channels.filter((c) => sel.has(c.id) && c.logo_file_id).slice(0, 10);
      return (
        <div className="mt-2" data-testid={`channels-done-${line.item_id}`}>
          <p className="text-[13px] font-semibold text-emerald-700">{tr(`${line.count} chaînes choisies pour ${line.name}.`, `${line.count} channels chosen for ${line.name}.`)}</p>
          {logos.length > 0 && (
            <div className="mt-2.5 grid grid-cols-5 gap-1.5" aria-hidden="true">
              {logos.map((c) => (
                <div key={c.id} className="h-9 rounded-[8px] bg-[#F6F4F0] p-1.5 flex items-center justify-center">
                  <img src={fileUrl(c.logo_file_id)} alt="" loading="lazy" className="max-w-full max-h-full object-contain" />
                </div>
              ))}
            </div>
          )}
          <button type="button" onClick={() => setOpen(true)} data-testid={`channels-edit-${line.item_id}`}
            className="mt-3 h-9 px-3.5 rounded-lg bg-[#F4F1EC] text-[13px] font-bold hover:bg-[#EAE5DC] transition-colors">
            {tr("Modifier mon choix", "Change my selection")}
          </button>
        </div>
      );
    }
    return (
      <div className="mt-1 text-xs" data-testid={`channels-done-${line.item_id}`}>
        <p className="text-emerald-700">{tr(`${line.count} chaînes choisies pour ${line.name}.`, `${line.count} channels chosen for ${line.name}.`)}</p>
        <button type="button" onClick={() => setOpen(true)} data-testid={`channels-edit-${line.item_id}`} className="min-h-10 text-primary underline underline-offset-2">
          {tr("Modifier mon choix", "Change my selection")}
        </button>
      </div>
    );
  }
  if (premium) {
    return (
      <div className="mt-3 space-y-4" data-testid={`channels-picker-${line.item_id}`}>
        <div className="rounded-2xl bg-[#F6F4F0] p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[14px] font-bold leading-snug">{tr(`Choisissez ${line.count} chaînes`, `Choose ${line.count} channels`)}</p>
            <span className="px-2.5 py-1 rounded-full bg-white text-[13px] font-bold tabular-nums" data-testid={`channels-count-${line.item_id}`}>{sel.size}/{line.count}</span>
          </div>
          <div className="mt-2.5 h-1.5 rounded-full bg-[#DAD5CC]" aria-hidden="true">
            <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${(100 * sel.size) / line.count}%` }} />
          </div>
          {coreCount > 0 && (
            <p className="mt-2.5 text-[12.5px] text-muted-foreground leading-snug" data-testid={`channels-core-hint-${line.item_id}`}>
              {tr(`Les ${coreCount} chaînes du cœur sont déjà incluses : choisissez parmi les autres.`, `The ${coreCount} core channels are already included: choose from the others.`)}
            </p>
          )}
          {hasSoon && <p className="mt-1.5 text-[12.5px] text-muted-foreground leading-snug" data-testid={`channels-soon-hint-${line.item_id}`}>{soonHint}</p>}
          {full && <p className="mt-1.5 text-[12.5px] font-semibold leading-snug">{tr("C'est complet : retirez une chaîne pour en choisir une autre.", "That's complete: remove a channel to choose another one.")}</p>}
        </div>
        <ChannelTiles channels={channels} selected={sel} onToggle={toggle} full={full} premium />
        {error && <p className="text-sm font-medium text-rose-700" data-testid={`channels-error-${line.item_id}`}>{error}</p>}
        {/* bottom-24 : au-dessus de la barre fixe « Vous avez accepté » (la page réserve pb-40 pour elle). */}
        <button type="button" onClick={save} disabled={saving || sel.size !== line.count} data-testid={`channels-save-${line.item_id}`}
          className="sticky bottom-24 z-10 w-full h-12 rounded-xl bg-primary text-white font-bold flex items-center justify-center gap-2 shadow-[0_14px_30px_-14px_rgba(18,18,18,0.6)] disabled:opacity-60 transition">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={2.5} />} {tr("Enregistrer mon choix", "Save my selection")} ({sel.size}/{line.count})
        </button>
      </div>
    );
  }
  return (
    <div className="mt-2 space-y-3" data-testid={`channels-picker-${line.item_id}`}>
      <p className="text-xs text-muted-foreground">
        {line.name}{tr(" : ", ": ")}{tr(`choisissez ${line.count} chaînes`, `choose ${line.count} channels`)} —{" "}
        <span className="font-semibold text-foreground" data-testid={`channels-count-${line.item_id}`}>{sel.size}/{line.count}</span>
        {coreCount > 0 && <span className="block mt-0.5" data-testid={`channels-core-hint-${line.item_id}`}>{tr(`Les ${coreCount} chaînes du cœur sont déjà incluses : choisissez parmi les autres.`, `The ${coreCount} core channels are already included: choose from the others.`)}</span>}
        {hasSoon && <span className="block mt-0.5" data-testid={`channels-soon-hint-${line.item_id}`}>{soonHint}</span>}
      </p>
      <ChannelTiles channels={channels} selected={sel} onToggle={toggle} full={full} />
      {error && <p className="text-xs text-rose-600" data-testid={`channels-error-${line.item_id}`}>{error}</p>}
      {/* bottom-28 : au-dessus de la barre fixe « Vous avez accepté » (la page réserve pb-32 pour elle). */}
      <button type="button" onClick={save} disabled={saving || sel.size !== line.count} data-testid={`channels-save-${line.item_id}`}
        className="sticky bottom-28 inline-flex items-center gap-1.5 h-10 px-4 rounded-sm bg-primary text-white text-sm font-medium shadow-md disabled:opacity-60">
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {tr("Enregistrer mon choix", "Save my selection")} ({sel.size}/{line.count})
      </button>
    </div>
  );
}

function DossierStep({ done, label, children, premium = false, n = 1, current = false }) {
  const tr = useTr();
  if (premium) {
    // Même pastille que « Votre parcours » / « Et ensuite ? » : faite, en cours (couleur de la société), à venir.
    return (
      <li className="flex gap-3.5 py-4 border-t border-[#EFEBE4] first:border-t-0 first:pt-0" aria-current={current ? "step" : undefined}>
        <StepDot state={done ? "done" : current ? "current" : "todo"} n={n} />
        <div className="flex-1 min-w-0 pt-0.5">
          <p className="font-bold text-[15px] leading-snug">{label}{done && <span className="sr-only"> {tr("(fait)", "(done)")}</span>}</p>
          {children}
        </div>
      </li>
    );
  }
  return (
    <li className="flex items-start gap-3">
      <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-emerald-500 text-white" : "border-2 border-border"}`}>
        {done && <Check className="w-3 h-3" />}
      </span>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${done ? "text-foreground" : "text-muted-foreground"}`}>{label}</p>
        {children}
      </div>
    </li>
  );
}

// Même logique que SAFE_REDIRECT_PREFIXES / safeRedirect (lib/api.js) pour Stripe/PayPal,
// dupliquée ici en local : le mandat SEPA est le 3e redirect externe de cette page mais
// GoCardless n'est pas dans la liste blanche partagée (hors périmètre de ce chantier — api.js
// n'est pas modifié). Ne jamais assigner window.location.href à une URL serveur sans la valider.
const GOCARDLESS_URL_PREFIXES = ["https://pay.gocardless.com/", "https://pay-sandbox.gocardless.com/"];

/**
 * Panneau post-acceptation : synthèse des étapes du dossier (signature, pièces, mandat SEPA).
 * Le dépôt des pièces se fait dans DocumentsBlock ci-dessus (déjà visible, statut 'acceptée'
 * inclus dans canUploadDocs) — ce panneau ne fait qu'en refléter l'avancement et héberge
 * l'action du mandat SEPA, qui n'a nulle part ailleurs où vivre.
 */
function DossierPanel({ publicId, dossier, channels, selections, onReload, premium = false, requiredDocuments = [], documents = [] }) {
  const tr = useTr();
  const lang = useLang();
  const [sepaStarting, setSepaStarting] = useState(false);
  const [sepaError, setSepaError] = useState("");

  const startSepaMandate = async () => {
    setSepaStarting(true);
    setSepaError("");
    try {
      const { data } = await api.post(`/public/proposals/${publicId}/sepa-mandate`);
      const url = data.authorisation_url;
      if (url && GOCARDLESS_URL_PREFIXES.some((prefix) => url.startsWith(prefix))) {
        window.location.href = url;
        return;
      }
      setSepaError(tr("Lien de signature indisponible pour le moment.", "Signing link unavailable at the moment."));
    } catch (e) {
      setSepaError(formatAxiosError(e));
    } finally {
      setSepaStarting(false);
    }
  };

  const docsRequired = dossier.documents.required.length;
  const docsDone = docsRequired - dossier.documents.missing.length;
  const sepa = dossier.sepa;
  const sepaAvailableDate = sepa.available_at ? new Date(sepa.available_at) : null;
  const sepaNotYetOpen = !!(sepaAvailableDate && sepaAvailableDate.getTime() > Date.now());

  const sepaDate = sepaAvailableDate?.toLocaleDateString(dateLocale(lang), { day: "numeric", month: "long", year: "numeric" });
  const sepaBody = sepa.done ? (
    <p className={premium ? "text-[13px] font-semibold text-emerald-700 mt-1" : "text-xs text-emerald-700 mt-1"}>{tr("Mandat signé.", "Mandate signed.")}</p>
  ) : sepaNotYetOpen ? (
    <p className={premium ? "text-[13px] text-muted-foreground mt-1 leading-snug" : "text-xs text-muted-foreground mt-1"} data-testid="sepa-not-yet-open">
      {tr(`Vous pourrez signer votre mandat à partir du ${sepaDate} (délai légal).`, `You can sign your mandate from ${sepaDate} (statutory period).`)}
    </p>
  ) : (
    <div className={premium ? "mt-2.5" : "mt-1.5"}>
      {sepa.failed && (
        <p className={`text-rose-600 mb-1.5 ${premium ? "text-[13px]" : "text-xs"}`} data-testid="sepa-failed">
          {tr("Le précédent mandat n'a pas abouti. Vous pouvez réessayer.", "The previous mandate did not go through. You can try again.")}
        </p>
      )}
      <button onClick={startSepaMandate} disabled={sepaStarting} data-testid="sepa-start"
        className={premium
          ? "w-full sm:w-auto h-12 px-5 rounded-xl bg-primary text-white font-bold flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-50 transition"
          : "inline-flex items-center gap-1.5 h-11 sm:h-9 px-4 rounded-sm bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"}>
        {sepaStarting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        {premium ? <>{tr("Signer mon mandat SEPA", "Sign my SEPA mandate")} <ArrowRight className="w-4 h-4" /></> : tr(" Signer mon mandat de prélèvement SEPA", " Sign my SEPA direct debit mandate")}
      </button>
      {sepaError && <p className={`text-rose-600 mt-1.5 ${premium ? "text-[13px]" : "text-xs"}`} data-testid="sepa-action-error">{sepaError}</p>}
    </div>
  );
  // Ordre du parcours : pièces (exigées pour signer), signature, mandat (juste après, sauf délai activé), chaînes.
  const items = [
    ...(docsRequired > 0 ? [{
      key: "docs", done: dossier.documents.complete, label: `${tr("Pièces", "Documents")} (${docsDone}/${docsRequired})`,
      // Premium : pièces encore manquantes (proposition signée avant qu'elles soient exigées) déposées ici même.
      body: premium && !dossier.documents.complete && requiredDocuments.length > 0 && (
        <div className="mt-2.5 rounded-xl border border-[#EFEBE4]">
          <DocumentsBlock variant="sheet" publicId={publicId} requiredDocuments={requiredDocuments} documents={documents} onUploaded={onReload} />
        </div>
      ),
    }] : []),
    { key: "sign", done: dossier.signed, label: "Signature" },
    ...(sepa.required ? [{ key: "sepa", done: sepa.done, waiting: sepaNotYetOpen, label: tr("Mandat de prélèvement SEPA", "SEPA direct debit mandate"), body: sepaBody }] : []),
    ...(dossier.channels?.required ? [{
      key: "channels", done: dossier.channels.done, label: tr("Choix des chaînes TV", "TV channel selection"),
      body: dossier.channels.lines.map((l) => (
        <ChannelPicker key={l.item_id} publicId={publicId} line={l} coreCount={channels.filter((c) => channelTier(c) === "core").length}
          channels={channels.filter((c) => channelTier(c) === "choice")} premium={premium}
          initial={selections[l.item_id]} onSaved={onReload} />
      )),
    }] : []),
  ];
  const current = items.findIndex((it) => !it.done && !it.waiting);

  return (
    <div id="dossier" className={premium ? "scroll-mt-24 rounded-[20px] bg-white max-w-3xl mx-auto lg:mx-0 p-5 sm:p-8" : `bg-white border border-border shadow-sm rounded-sm max-w-3xl mx-auto lg:mx-0 mt-6 p-5 sm:p-6 lg:p-8 ${FULL_BLEED}`} data-testid="dossier-panel">
      {premium ? (
        <h2 className="font-display font-extrabold text-[1.55rem] leading-[1.08] tracking-[-0.02em] mb-4">{tr("Finaliser votre dossier", "Complete your setup")}</h2>
      ) : (
        <div className="flex items-center gap-2 mb-4">
          <Landmark className="w-5 h-5 text-primary" />
          <h2 className="font-heading text-xl font-bold">{tr("Finaliser votre dossier", "Complete your setup")}</h2>
        </div>
      )}

      {dossier.complete ? (
        <p className="text-sm font-medium text-emerald-700" data-testid="dossier-complete">{tr("Dossier complet, merci !", "All done, thank you!")}</p>
      ) : (
        <ol className={premium ? "" : "space-y-4"}>
          {items.map((it, i) => (
            <DossierStep key={it.key} done={it.done} label={it.label} premium={premium} n={i + 1} current={i === current}>{it.body}</DossierStep>
          ))}
        </ol>
      )}
    </div>
  );
}
